/**
 * Snows ProAm — Project Athlete CRM  (Full Rebuild — Mobile Responsive)
 * ───────────────────────────────────────────────────────────────────────
 * ✅ Light / Dark mode
 * ✅ Activity Feed capped at 8 most recent entries
 * ✅ "Secondary Schools" everywhere
 * ✅ Add / Edit leads
 * ✅ Profile Switcher — all profiles shown on login, pick any
 * ✅ Edit Profile — change name, colour, emoji, PIN from within app
 * ✅ FULLY MOBILE RESPONSIVE (hamburger nav, card views on mobile, etc.)
 * ✅ FIX: No more overlap between Log Call and Edit button on mobile cards
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { supabase } from '../supabaseClient'

/* ═══════════════════════════════════════════════════════════
   THEME
═══════════════════════════════════════════════════════════ */
const DARK = {
  mode: 'dark',
  bg: '#080B12', surface: '#0E1220', card: '#111827', card2: '#141c2e',
  border: 'rgba(12,105,200,.15)', border2: 'rgba(255,255,255,.07)',
  accent: '#0C69C8', accent2: '#38BDF8', green: '#34D399', amber: '#FBBF24',
  rose: '#F87171', purple: '#A78BFA', text: '#F0F4FF',
  muted: 'rgba(240,244,255,.45)', muted2: 'rgba(240,244,255,.22)',
  headerBg: 'rgba(8,11,18,.92)',
  inputBg: 'rgba(255,255,255,.04)',
  inputBorder: 'rgba(255,255,255,.1)',
  scrollThumb: 'rgba(12,105,200,.3)',
  tableHead: '#0E1220',
  tableHover: 'rgba(255,255,255,.025)',
  tagBg: 'rgba(255,255,255,.06)',
}

const LIGHT = {
  mode: 'light',
  bg: '#F0F4FA', surface: '#FFFFFF', card: '#FFFFFF', card2: '#F8FAFD',
  border: 'rgba(12,105,200,.12)', border2: 'rgba(10,29,68,.09)',
  accent: '#0C69C8', accent2: '#0284C7', green: '#059669', amber: '#D97706',
  rose: '#DC2626', purple: '#7C3AED', text: '#0F1629',
  muted: '#5A6B8A', muted2: '#9BADC8',
  headerBg: 'rgba(240,244,250,.95)',
  inputBg: 'rgba(10,29,68,.03)',
  inputBorder: 'rgba(10,29,68,.12)',
  scrollThumb: 'rgba(12,105,200,.25)',
  tableHead: '#F5F7FB',
  tableHover: 'rgba(12,105,200,.03)',
  tagBg: 'rgba(10,29,68,.06)',
}

/* ═══════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════ */
const PROFILE_COLORS = ['#0C69C8','#34D399','#FBBF24','#F87171','#A78BFA','#F472B6','#38BDF8','#FB923C']
const PROFILE_EMOJIS = ['⚡','🏃','🎾','🏆','🔥','⭐','💪','🚀','🎯','🦁','🐆','🔑']

const STATUS_MAP = {
  '':             { label:'+ Log Call',     bg:'rgba(255,255,255,.07)', fg:'rgba(240,244,255,.4)' },
  's-done':       { label:'✓ Called',       bg:'rgba(52,211,153,.15)',  fg:'#34D399' },
  's-callback':   { label:'↩ Callback',     bg:'rgba(56,189,248,.15)',  fg:'#38BDF8' },
  's-interested': { label:'⭐ Interested',  bg:'rgba(251,191,36,.15)',  fg:'#FBBF24' },
  's-booked':     { label:'📅 Demo Booked', bg:'rgba(12,105,200,.2)',   fg:'#60a5fa' },
  's-no':         { label:'— No Reply',     bg:'rgba(248,113,113,.15)', fg:'#F87171' },
}
const STATUS_CYCLE = ['','s-done','s-callback','s-interested','s-booked','s-no']

const SCHOOL_COUNTIES = [
  'Antrim','Armagh','Carlow','Cavan','Clare','Cork','Derry','Donegal','Down',
  'Dublin','Fermanagh','Galway','Kerry','Kildare','Kilkenny','Laois','Leitrim',
  'Limerick','Longford','Louth','Mayo','Meath','Monaghan','Offaly','Roscommon',
  'Sligo','Tipperary','Tyrone','Waterford','Westmeath','Wexford','Wicklow',
]

const PADEL_COUNTIES = [
  'Antrim','Armagh','Carlow','Cavan','Clare','Cork','Derry','Donegal','Down',
  'Dublin','Fermanagh','Galway','Kerry','Kildare','Kilkenny','Laois','Leitrim',
  'Limerick','Longford','Louth','Mayo','Meath','Monaghan','National','Offaly',
  'Roscommon','Sligo','Tipperary','Tyrone','Waterford','Westmeath','Wexford','Wicklow',
]

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — fully mobile responsive
═══════════════════════════════════════════════════════════ */
const makeCSS = (C) => `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{font-size:16px;}
body{font-family:'DM Sans',sans-serif;background:${C.bg};color:${C.text};min-height:100vh;overflow-x:hidden;transition:background .25s,color .25s;}
a{color:inherit;text-decoration:none;}
button{font-family:'DM Sans',sans-serif;cursor:pointer;}
input,select,textarea{font-family:'DM Sans',sans-serif;}
::-webkit-scrollbar{width:4px;}
::-webkit-scrollbar-thumb{background:${C.scrollThumb};border-radius:4px;}

@keyframes fadeUp  {from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse   {0%,100%{opacity:.5}50%{opacity:1}}
@keyframes glow    {0%,100%{box-shadow:0 0 20px rgba(12,105,200,.3)}50%{box-shadow:0 0 50px rgba(12,105,200,.5),0 0 70px rgba(56,189,248,.2)}}
@keyframes toastIn {from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
@keyframes shake   {0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
@keyframes spin    {to{transform:rotate(360deg)}}
@keyframes slideUp {from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}

.fu {animation:fadeUp .5s cubic-bezier(.16,1,.3,1) both}
.fu2{animation:fadeUp .5s .08s cubic-bezier(.16,1,.3,1) both}
.fu3{animation:fadeUp .5s .16s cubic-bezier(.16,1,.3,1) both}
.shake{animation:shake .35s ease both}

/* ── Table (desktop only) ── */
.tbl{width:100%;border-collapse:separate;border-spacing:0;}
.tbl thead th{background:${C.tableHead};padding:10px 14px;text-align:left;font-size:10px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.8px;border-bottom:1px solid ${C.border2};white-space:nowrap;cursor:pointer;user-select:none;transition:color .15s;}
.tbl thead th:hover{color:${C.accent2};}
.tbl tbody tr{cursor:pointer;transition:background .1s;}
.tbl tbody tr:not(:last-child) td{border-bottom:1px solid ${C.border2};}
.tbl tbody tr:hover{background:${C.tableHover};}
.tbl td{padding:9px 14px;font-size:13px;color:${C.text};vertical-align:middle;}

/* ── Lead Card (mobile) ── */
.lead-card{background:${C.card};border:1px solid ${C.border2};border-radius:16px;padding:16px;margin-bottom:10px;cursor:pointer;transition:all .15s;active:opacity:.8;}
.lead-card:hover{border-color:${C.border};transform:translateY(-1px);}

/* ── Inputs ── */
.ninp{background:transparent;border:none;border-bottom:1px solid ${C.border2};color:${C.muted};font-size:12px;width:130px;padding:3px 4px;outline:none;transition:border-color .2s,color .2s;}
.ninp:focus{border-color:${C.accent};color:${C.text};}
.ninp::placeholder{color:${C.muted2};}

.finp{width:100%;background:${C.inputBg};border:1px solid ${C.inputBorder};border-radius:10px;padding:10px 12px;color:${C.text};font-size:13px;outline:none;transition:border-color .2s;}
.finp:focus{border-color:${C.accent};}
.finp::placeholder{color:${C.muted2};}

.fsel{width:100%;background:${C.inputBg};border:1px solid ${C.inputBorder};border-radius:10px;padding:10px 12px;color:${C.text};font-size:13px;outline:none;cursor:pointer;transition:border-color .2s;appearance:none;-webkit-appearance:none;}
.fsel:focus{border-color:${C.accent};}

.mnotes{width:100%;background:${C.inputBg};border:1px solid ${C.inputBorder};border-radius:10px;padding:10px 12px;color:${C.text};font-size:13px;resize:vertical;min-height:80px;outline:none;transition:border-color .2s;}
.mnotes:focus{border-color:${C.accent};}

.pin-dot{width:14px;height:14px;border-radius:50%;border:2px solid rgba(240,244,255,.3);background:transparent;transition:background .15s,border-color .15s;}
.pin-dot.filled{background:#38BDF8;border-color:#38BDF8;}

.nav-btn{padding:6px 13px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:1px solid transparent;transition:all .15s;}
.theme-btn{transition:transform .2s;}
.theme-btn:hover{transform:rotate(15deg) scale(1.1);}

.profile-card{transition:all .2s;cursor:pointer;}
.profile-card:hover{transform:translateY(-3px);}

/* ── Mobile Bottom Nav ── */
.bottom-nav{display:none;}

/* ── Responsive breakpoints ── */

/* Tablet & below: hide desktop table, show cards */
@media(max-width:900px){
  .desktop-table{display:none!important;}
  .mobile-cards{display:block!important;}
  .tbl-wrap{display:none!important;}
}
@media(min-width:901px){
  .mobile-cards{display:none!important;}
}

/* Mobile (≤640px) */
@media(max-width:640px){
  /* Header */
  .header-nav{display:none!important;}
  .header-right-desktop{display:none!important;}
  .header-right-mobile{display:flex!important;}
  .bottom-nav{display:flex!important;position:fixed;bottom:0;left:0;right:0;z-index:100;background:${C.headerBg};backdrop-filter:blur(20px);border-top:1px solid ${C.border2};height:58px;align-items:stretch;}
  .bottom-nav-btn{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;border:none;background:none;color:${C.muted};font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;padding:6px 0;transition:color .15s;}
  .bottom-nav-btn.active{color:${C.accent2};}
  .bottom-nav-btn .icon{font-size:18px;}
  body{padding-bottom:58px;}

  /* Dashboard stat grid: 2 col on mobile */
  .stat-grid{grid-template-columns:1fr 1fr!important;}
  .stat-grid .stat-card{padding:14px!important;}

  /* Dashboard hero */
  .dash-hero{padding:20px!important;}
  .dash-hero h1{font-size:20px!important;}
  .dash-tags{flex-wrap:wrap;gap:6px!important;}
  .dash-tags > div{font-size:11px!important;padding:5px 10px!important;}

  /* Dashboard bottom cards */
  .dash-bottom-grid{grid-template-columns:1fr!important;}

  /* Page padding */
  .page-wrap{padding:16px 14px 72px!important;}

  /* Filters */
  .filter-row{flex-direction:column!important;gap:8px!important;}
  .filter-row .search-wrap{width:100%!important;}
  .filter-row select{width:100%!important;}

  /* Lead modal */
  .lead-modal-inner{padding:20px 16px!important;border-radius:20px 20px 0 0!important;max-height:92vh!important;}
  .lead-modal-grid{grid-template-columns:1fr!important;}
  .lead-modal-status{flex-wrap:wrap!important;}

  /* Page header */
  .page-header{flex-direction:column!important;align-items:flex-start!important;gap:12px!important;}
  .page-header .add-btn{width:100%!important;justify-content:center!important;}

  /* Lead form modal */
  .lform-grid{grid-template-columns:1fr!important;}
  .lform-grid > div[style*="1/-1"]{grid-column:1/-1!important;}
  .lform-inner{padding:20px 16px!important;border-radius:20px 20px 0 0!important;}
  .lform-btns{flex-direction:column!important;}

  /* Profile switcher */
  .prof-switcher-grid{grid-template-columns:repeat(2,1fr)!important;}

  /* Edit profile modal */
  .edit-prof-inner{border-radius:20px 20px 0 0!important;padding:28px 20px!important;}

  /* Lead card action row — prevent overflow */
  .lead-card-actions{flex-wrap:nowrap!important;overflow:hidden;}
  .lead-card-actions .status-edit-row{flex-shrink:0;}
}

/* Medium tablet */
@media(min-width:641px) and (max-width:900px){
  .header-nav{display:none!important;}
  .header-right-desktop{display:none!important;}
  .header-right-mobile{display:flex!important;}
  .bottom-nav{display:flex!important;position:fixed;bottom:0;left:0;right:0;z-index:100;background:${C.headerBg};backdrop-filter:blur(20px);border-top:1px solid ${C.border2};height:58px;align-items:stretch;}
  .bottom-nav-btn{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;border:none;background:none;color:${C.muted};font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;padding:6px 0;transition:color .15s;}
  .bottom-nav-btn.active{color:${C.accent2};}
  .bottom-nav-btn .icon{font-size:18px;}
  body{padding-bottom:58px;}
  .stat-grid{grid-template-columns:repeat(2,1fr)!important;}
  .page-wrap{padding:20px 16px 72px!important;}
  .filter-row{flex-wrap:wrap!important;gap:8px!important;}
}
`

/* ═══════════════════════════════════════════════════════════
   UTILS
═══════════════════════════════════════════════════════════ */
const lk = (pipeline, id) => `${pipeline === 'schools' ? 'sc' : pipeline === 'schools_uk' ? 'uk' : 'pd'}_${id}`
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()

function timeAgo(iso) {
  if (!iso) return ''
  const m = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m/60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h/24)}d ago`
}

async function hashPin(pin) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pin))
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join('')
}

/* Hook: is screen mobile? */
function useIsMobile(bp=640) {
  const [mob, setMob] = useState(() => typeof window !== 'undefined' && window.innerWidth <= bp)
  useEffect(()=>{
    const fn = () => setMob(window.innerWidth <= bp)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  },[bp])
  return mob
}

/* ═══════════════════════════════════════════════════════════
   SMALL COMPONENTS
═══════════════════════════════════════════════════════════ */
function Avatar({ p, size=28 }) {
  if (!p) return <div style={{width:size,height:size,borderRadius:size*.35,background:'#1e2740'}}/>
  return (
    <div style={{
      width:size, height:size, borderRadius:size*.35,
      background:p.color||'#0C69C8',
      display:'flex',alignItems:'center',justifyContent:'center',
      fontSize:size*.42, fontWeight:700, color:'#fff',
      fontFamily:"'Sora',sans-serif", flexShrink:0, userSelect:'none',
      border:`1.5px solid ${p.color||'#0C69C8'}40`,
    }}>
      {p.emoji || initials(p.display_name)}
    </div>
  )
}

function CallerTag({ state, profiles, myId, C }) {
  if (!state?.last_called_by || !state?.status) return null
  const caller = profiles[state.last_called_by]
  if (!caller) return null
  const isMe = state.last_called_by === myId
  return (
    <div style={{display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
      <div style={{width:18,height:18,borderRadius:5,background:caller.color||C.accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',fontWeight:700,flexShrink:0}}>
        {caller.emoji||initials(caller.display_name)}
      </div>
      <span style={{fontSize:11,color:C.muted,whiteSpace:'nowrap'}}>
        {isMe?'You':caller.display_name} · {timeAgo(state.last_called_at)}
      </span>
    </div>
  )
}

function StatusBtn({ status, onClick, C }) {
  const m = STATUS_MAP[status||'']||STATUS_MAP['']
  const fg = m.fg === 'rgba(240,244,255,.4)' ? C.muted : m.fg
  return (
    <button onClick={e=>{e.stopPropagation();onClick()}} style={{
      background:m.bg, color:fg,
      border:`1px solid ${fg}40`,
      borderRadius:7, padding:'5px 10px',
      fontSize:10, fontWeight:700, cursor:'pointer',
      whiteSpace:'nowrap', letterSpacing:'.3px', textTransform:'uppercase',
      transition:'all .15s',
    }}>{m.label}</button>
  )
}

function Toast({ toasts }) {
  return (
    <div style={{position:'fixed',top:66,right:12,zIndex:999,display:'flex',flexDirection:'column',gap:8,pointerEvents:'none',maxWidth:'calc(100vw - 24px)'}}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background:'#141c2e', border:'1px solid rgba(12,105,200,.3)',
          borderLeft:`3px solid ${t.color||'#0C69C8'}`,
          borderRadius:11, padding:'10px 14px',
          display:'flex', alignItems:'center', gap:10,
          animation:'toastIn .3s ease both',
          boxShadow:'0 8px 32px rgba(0,0,0,.6)', maxWidth:300,
        }}>
          <div style={{width:26,height:26,borderRadius:7,background:t.color||'#0C69C8',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,flexShrink:0}}>{t.emoji||'⚡'}</div>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:'#F0F4FF'}}>{t.title}</div>
            <div style={{fontSize:11,color:'rgba(240,244,255,.45)',marginTop:1}}>{t.body}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function LoadingScreen({ msg='Loading…', C }) {
  return (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <div style={{width:40,height:40,borderRadius:'50%',border:`3px solid ${C.border2}`,borderTopColor:C.accent,animation:'spin .75s linear infinite'}}/>
      <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:900,color:C.text}}>
        Snows<span style={{color:C.accent2}}>ProAm</span>
      </div>
      <div style={{fontSize:13,color:C.muted}}>{msg}</div>
    </div>
  )
}

function ThemeToggle({ isDark, onToggle }) {
  return (
    <button className="theme-btn" onClick={onToggle} title="Toggle theme" style={{
      width:34,height:34,borderRadius:9,
      background:'rgba(255,255,255,.06)',
      border:'1px solid rgba(255,255,255,.1)',
      color:'#F0F4FF',display:'flex',alignItems:'center',
      justifyContent:'center',fontSize:15,flexShrink:0,
    }}>
      {isDark ? '☀️' : '🌙'}
    </button>
  )
}

/* ═══════════════════════════════════════════════════════════
   PIN PAD
═══════════════════════════════════════════════════════════ */
function PinPad({ length=4, onChange, shake=false, C }) {
  const [digits, setDigits] = useState([])

  const press = (d) => {
    if (digits.length >= length) return
    const next = [...digits, d]
    setDigits(next)
    if (next.length === length) onChange(next.join(''))
  }
  const del = () => setDigits(d => d.slice(0,-1))

  useEffect(() => {
    if (shake) { const t = setTimeout(() => setDigits([]), 400); return () => clearTimeout(t) }
  }, [shake])

  return (
    <div>
      <div style={{display:'flex',justifyContent:'center',gap:12,marginBottom:28}}>
        {Array.from({length}).map((_,i) => (
          <div key={i} className={`pin-dot${i < digits.length ? ' filled' : ''}`}/>
        ))}
      </div>
      <div className={shake ? 'shake' : ''} style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,maxWidth:240,margin:'0 auto'}}>
        {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k,i) => (
          <button key={i} onClick={() => k === '⌫' ? del() : k !== '' ? press(String(k)) : null} disabled={k === ''}
            style={{height:56,borderRadius:14,background:k===''?'transparent':C.inputBg,border:`1px solid ${k===''?'transparent':C.inputBorder}`,color:k==='⌫'?C.muted:C.text,fontSize:k==='⌫'?20:22,fontFamily:"'Sora',sans-serif",fontWeight:700,cursor:k===''?'default':'pointer',transition:'background .1s'}}
          >{k}</button>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   AUTH GATE
═══════════════════════════════════════════════════════════ */
function AccessGate({ onSuccess, C }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState(false)

  const submit = () => {
    if (code === 'projectAthlete') {
      localStorage.setItem('crm-access', 'ok')
      onSuccess()
    } else {
      setErr(true)
      setTimeout(() => setErr(false), 500)
    }
  }

  return (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:40,width:'100%',maxWidth:360,textAlign:'center'}}>
        <div style={{fontSize:28,marginBottom:10}}>🔐</div>
        <div style={{fontWeight:800,fontSize:18,marginBottom:10,color:C.text}}>Enter Access Code</div>
        <input
          value={code}
          onChange={e=>setCode(e.target.value)}
          onKeyDown={e=>e.key==='Enter' && submit()}
          placeholder="Access code"
          className="finp"
          style={{marginBottom:12,textAlign:'center',letterSpacing:2}}
        />
        {err && <div style={{color:C.rose,fontSize:12,marginBottom:10}}>Wrong code</div>}
        <button onClick={submit} style={{width:'100%',padding:12,borderRadius:10,background:C.accent,color:'#fff',border:'none',fontWeight:700,fontSize:14}}>
          Enter
        </button>
      </div>
    </div>
  )
}

function NotAuthed({ C }) {
  return (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
      <div className="fu" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:24,padding:'44px 40px',width:'100%',maxWidth:400,textAlign:'center',boxShadow:'0 0 60px rgba(12,105,200,.18),0 24px 80px rgba(0,0,0,.65)'}}>
        <div style={{fontSize:48,marginBottom:16}}>🔒</div>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,marginBottom:8,color:C.text}}>Access Restricted</div>
        <div style={{fontSize:13,color:C.muted,lineHeight:1.6}}>You need to be signed in to your SnowsProAm account to access the CRM.</div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   PROFILE SWITCHER
═══════════════════════════════════════════════════════════ */
function ProfileSwitcher({ allProfiles, onSelect, onCreateNew, C }) {
  return (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20,overflowY:'auto'}}>
      <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 50% 40%,rgba(12,105,200,.12) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="fu" style={{width:'100%',maxWidth:480,position:'relative'}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:900,marginBottom:6,color:C.text}}>
            Snows<span style={{color:C.accent2}}>ProAm</span>
          </div>
          <div style={{fontSize:12,color:C.muted,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>🏆 Who's using the CRM?</div>
        </div>

        <div className="prof-switcher-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:12,marginBottom:20}}>
          {allProfiles.map(p => (
            <div key={p.id} className="profile-card" onClick={() => onSelect(p)}
              style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:18,padding:'20px 16px',textAlign:'center',boxShadow:'0 2px 12px rgba(0,0,0,.08)'}}>
              <div style={{display:'flex',justifyContent:'center',marginBottom:12}}>
                <div style={{width:56,height:56,borderRadius:16,background:p.color||C.accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,border:`2px solid ${p.color||C.accent}40`}}>
                  {p.emoji || initials(p.display_name)}
                </div>
              </div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{p.display_name}</div>
              <div style={{fontSize:11,color:C.muted}}>Enter PIN →</div>
            </div>
          ))}

          <div className="profile-card" onClick={onCreateNew}
            style={{background:'transparent',border:`1.5px dashed ${C.border2}`,borderRadius:18,padding:'20px 16px',textAlign:'center'}}>
            <div style={{display:'flex',justifyContent:'center',marginBottom:12}}>
              <div style={{width:56,height:56,borderRadius:16,background:C.inputBg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,border:`1px solid ${C.border2}`}}>
                ➕
              </div>
            </div>
            <div style={{fontSize:13,fontWeight:700,color:C.muted,marginBottom:3}}>New Profile</div>
            <div style={{fontSize:11,color:C.muted2}}>Add yourself</div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   PROFILE SETUP (new profile)
═══════════════════════════════════════════════════════════ */
function ProfileSetup({ authUser, onComplete, onBack, C }) {
  const [step,  setStep]  = useState('details')
  const [name,  setName]  = useState('')
  const [color, setColor] = useState(PROFILE_COLORS[0])
  const [emoji, setEmoji] = useState(PROFILE_EMOJIS[0])
  const [pin1,  setPin1]  = useState('')
  const [shake, setShake] = useState(false)
  const [busy,  setBusy]  = useState(false)
  const [err,   setErr]   = useState('')

  const goToPin = () => { if (!name.trim()) { setErr('Enter your name to continue'); return }; setErr(''); setStep('pin') }
  const handlePin1 = (p) => { setPin1(p); setStep('confirm') }
  const handleConfirm = async (p) => {
    if (p !== pin1) { setShake(true); setErr("PINs don't match — try again"); setTimeout(() => { setShake(false); setStep('pin'); setPin1(''); setErr('') }, 800); return }
    setBusy(true); setErr('')
    const hash = await hashPin(p)
    const id = crypto.randomUUID()
    const profile = { id, auth_user_id: authUser.id, display_name: name.trim(), color, emoji, pin_hash: hash }
    const { error } = await supabase.from('crm_profiles').insert(profile)
    if (error) { setErr('Save failed — ' + error.message); setBusy(false); return }
    onComplete(profile)
  }

  const wrap = children => (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16,overflowY:'auto'}}>
      <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 50% 40%,rgba(12,105,200,.12) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="fu" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:24,padding:'36px 28px',width:'100%',maxWidth:420,position:'relative',boxShadow:'0 0 60px rgba(12,105,200,.18)',marginTop:'auto',marginBottom:'auto'}}>
        <div style={{textAlign:'center',marginBottom:24}}>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:22,fontWeight:900,marginBottom:4,color:C.text}}>Snows<span style={{color:C.accent2}}>ProAm</span></div>
          <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:1,color:C.muted}}>🏆 Create Your Profile</div>
        </div>
        {children}
        {onBack && <button onClick={onBack} style={{width:'100%',marginTop:12,padding:10,background:'none',border:`1px solid ${C.border2}`,borderRadius:10,color:C.muted,fontSize:12,cursor:'pointer'}}>← Back to profiles</button>}
      </div>
    </div>
  )

  if (step === 'details') return wrap(<>
    <div style={{display:'flex',justifyContent:'center',marginBottom:20}}>
      <div style={{width:64,height:64,borderRadius:18,background:color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,animation:'glow 3s ease-in-out infinite',transition:'background .25s'}}>{emoji}</div>
    </div>
    <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:6,textTransform:'uppercase'}}>Your Name</label>
    <input type="text" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&goToPin()} placeholder="e.g. Solomon" className="finp" style={{marginBottom:16}}/>
    <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:8,textTransform:'uppercase'}}>Your Colour</label>
    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
      {PROFILE_COLORS.map(c=><div key={c} onClick={()=>setColor(c)} style={{width:30,height:30,borderRadius:8,background:c,cursor:'pointer',border:`2.5px solid ${color===c?'#fff':'transparent'}`,transform:color===c?'scale(1.18)':'scale(1)',transition:'all .15s'}}/>)}
    </div>
    <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:8,textTransform:'uppercase'}}>Your Icon</label>
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:20}}>
      {PROFILE_EMOJIS.map(em=><div key={em} onClick={()=>setEmoji(em)} style={{width:36,height:36,borderRadius:10,cursor:'pointer',background:emoji===em?`${color}25`:C.inputBg,border:`1.5px solid ${emoji===em?color:C.inputBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,transition:'all .15s'}}>{em}</div>)}
    </div>
    {err&&<div style={{fontSize:12,color:C.rose,marginBottom:10,fontWeight:600}}>{err}</div>}
    <button onClick={goToPin} style={{width:'100%',padding:14,background:`linear-gradient(135deg,${color},${color}90)`,border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer'}}>Next — Set your PIN →</button>
  </>)

  if (step === 'pin') return wrap(<>
    <div style={{textAlign:'center',marginBottom:20}}>
      <div style={{fontSize:13,fontWeight:600,color:C.muted,marginBottom:4}}>Choose a 4-digit PIN</div>
    </div>
    <PinPad onChange={handlePin1} C={C}/>
  </>)

  return wrap(<>
    <div style={{textAlign:'center',marginBottom:20}}>
      <div style={{fontSize:13,fontWeight:600,color:C.muted}}>Confirm your PIN</div>
    </div>
    {err&&<div style={{fontSize:12,color:C.rose,marginBottom:12,fontWeight:600,textAlign:'center'}}>{err}</div>}
    <PinPad onChange={handleConfirm} shake={shake} C={C}/>
    {busy&&<div style={{textAlign:'center',marginTop:16,fontSize:12,color:C.muted}}>Saving…</div>}
  </>)
}

/* ═══════════════════════════════════════════════════════════
   PIN LOGIN
═══════════════════════════════════════════════════════════ */
function PinLogin({ profile, onSuccess, onBack, C }) {
  const [shake, setShake] = useState(false)
  const [err,   setErr]   = useState('')
  const [tries, setTries] = useState(0)

  const handlePin = async (pin) => {
    const hash = await hashPin(pin)
    if (hash === profile.pin_hash) { onSuccess() } else {
      setShake(true); setTries(t => t+1)
      setErr(tries >= 2 ? 'Too many attempts — sure this is your account?' : 'Wrong PIN')
      setTimeout(() => setShake(false), 400)
    }
  }

  return (
    <div style={{position:'fixed',inset:0,background:C.bg,display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16}}>
      <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 50% 40%,rgba(12,105,200,.12) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="fu" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:24,padding:'36px 28px',width:'100%',maxWidth:380,boxShadow:'0 0 60px rgba(12,105,200,.18)'}}>
        <div style={{textAlign:'center',marginBottom:24}}>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:22,fontWeight:900,marginBottom:14,color:C.text}}>Snows<span style={{color:C.accent2}}>ProAm</span></div>
          <div style={{display:'flex',justifyContent:'center'}}>
            <Avatar p={profile} size={64}/>
          </div>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginTop:12,marginBottom:4,color:C.text}}>{profile.display_name}</div>
          <div style={{fontSize:12,color:C.muted}}>Enter your PIN to continue</div>
        </div>
        {err&&<div style={{fontSize:12,color:C.rose,marginBottom:12,fontWeight:600,textAlign:'center'}}>{err}</div>}
        <PinPad onChange={handlePin} shake={shake} C={C}/>
        <button onClick={onBack} style={{width:'100%',marginTop:20,padding:10,background:'none',border:`1px solid ${C.border2}`,borderRadius:10,color:C.muted,fontSize:12,cursor:'pointer'}}>
          ← Back to profiles
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   EDIT PROFILE MODAL
═══════════════════════════════════════════════════════════ */
function EditProfileModal({ profile, onSave, onClose, C }) {
  const [step,    setStep]   = useState('details')
  const [name,    setName]   = useState(profile.display_name)
  const [color,   setColor]  = useState(profile.color || PROFILE_COLORS[0])
  const [emoji,   setEmoji]  = useState(profile.emoji || PROFILE_EMOJIS[0])
  const [pin1,    setPin1]   = useState('')
  const [shake,   setShake]  = useState(false)
  const [busy,    setBusy]   = useState(false)
  const [err,     setErr]    = useState('')
  const [success, setSuccess]= useState('')

  const saveDetails = async () => {
    if (!name.trim()) { setErr('Name is required'); return }
    setBusy(true); setErr('')
    const { error } = await supabase.from('crm_profiles').update({ display_name: name.trim(), color, emoji }).eq('id', profile.id)
    if (error) { setErr('Save failed'); setBusy(false); return }
    setBusy(false); setSuccess('Profile updated!')
    setTimeout(() => { onSave({ ...profile, display_name: name.trim(), color, emoji }); onClose() }, 800)
  }

  const handlePin1 = (p) => { setPin1(p); setStep('confirm') }
  const handleConfirm = async (p) => {
    if (p !== pin1) {
      setShake(true); setErr("PINs don't match")
      setTimeout(() => { setShake(false); setStep('pin'); setPin1(''); setErr('') }, 800)
      return
    }
    setBusy(true); setErr('')
    const hash = await hashPin(p)
    const { error } = await supabase.from('crm_profiles').update({ pin_hash: hash }).eq('id', profile.id)
    if (error) { setErr('Save failed'); setBusy(false); return }
    setBusy(false); setSuccess('PIN updated!')
    setTimeout(() => { onSave({ ...profile, pin_hash: hash }); onClose() }, 800)
  }

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:700,background:'rgba(0,0,0,.82)',backdropFilter:'blur(8px)',display:'flex',alignItems:'flex-end',justifyContent:'center',padding:0}}>
      <div className="fu edit-prof-inner" style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'24px 24px 0 0',padding:'32px 28px',width:'100%',maxWidth:480,position:'relative',boxShadow:'0 -8px 60px rgba(0,0,0,.5)',maxHeight:'92vh',overflowY:'auto'}}>
        <button onClick={onClose} style={{position:'absolute',top:16,right:16,background:'none',border:'none',color:C.muted,fontSize:18,cursor:'pointer',width:32,height:32,borderRadius:8}}>✕</button>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.7,marginBottom:8}}>✏️ Edit Profile</div>
          <div style={{display:'flex',justifyContent:'center'}}>
            <div style={{width:60,height:60,borderRadius:16,background:color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,transition:'background .2s'}}>{emoji}</div>
          </div>
        </div>

        {step === 'details' && <>
          <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:6,textTransform:'uppercase'}}>Display Name</label>
          <input type="text" value={name} onChange={e=>setName(e.target.value)} className="finp" style={{marginBottom:16}}/>
          <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:8,textTransform:'uppercase'}}>Colour</label>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
            {PROFILE_COLORS.map(c=><div key={c} onClick={()=>setColor(c)} style={{width:30,height:30,borderRadius:8,background:c,cursor:'pointer',border:`2.5px solid ${color===c?'#fff':'transparent'}`,transform:color===c?'scale(1.18)':'scale(1)',transition:'all .15s'}}/>)}
          </div>
          <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:8,textTransform:'uppercase'}}>Icon</label>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:20}}>
            {PROFILE_EMOJIS.map(em=><div key={em} onClick={()=>setEmoji(em)} style={{width:36,height:36,borderRadius:10,cursor:'pointer',background:emoji===em?`${color}25`:C.inputBg,border:`1.5px solid ${emoji===em?color:C.inputBorder}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,transition:'all .15s'}}>{em}</div>)}
          </div>
          {err&&<div style={{fontSize:12,color:C.rose,marginBottom:10,fontWeight:600}}>{err}</div>}
          {success&&<div style={{fontSize:12,color:C.green,marginBottom:10,fontWeight:600}}>{success}</div>}
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>setStep('pin')} style={{flex:1,padding:12,background:'none',border:`1px solid ${C.border2}`,borderRadius:11,color:C.muted,fontSize:13,cursor:'pointer'}}>🔑 Change PIN</button>
            <button onClick={saveDetails} disabled={busy} style={{flex:2,padding:12,background:'linear-gradient(135deg,#0C69C8,#0a47a0)',border:'none',borderRadius:11,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>{busy ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </>}

        {step === 'pin' && <>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted}}>Choose a new 4-digit PIN</div>
          </div>
          <PinPad onChange={handlePin1} C={C}/>
          <button onClick={()=>setStep('details')} style={{width:'100%',marginTop:14,padding:10,background:'none',border:`1px solid ${C.border2}`,borderRadius:10,color:C.muted,fontSize:12,cursor:'pointer'}}>← Back</button>
        </>}

        {step === 'confirm' && <>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted}}>Confirm your new PIN</div>
          </div>
          {err&&<div style={{fontSize:12,color:C.rose,marginBottom:12,fontWeight:600,textAlign:'center'}}>{err}</div>}
          <PinPad onChange={handleConfirm} shake={shake} C={C}/>
          {busy&&<div style={{textAlign:'center',marginTop:14,fontSize:12,color:C.muted}}>Saving…</div>}
        </>}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   HEADER — desktop nav + mobile top bar
═══════════════════════════════════════════════════════════ */
function Header({ profile, page, setPage, onlineUsers, unread, onFeed, onLock, onEditProfile, isDark, onTheme, C }) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header style={{position:'sticky',top:0,zIndex:100,height:58,padding:'0 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,background:C.headerBg,backdropFilter:'blur(20px)',borderBottom:`1px solid ${C.border2}`}}>
      {/* Logo */}
      <div style={{fontFamily:"'Sora',sans-serif",fontSize:17,fontWeight:900,letterSpacing:-.5,display:'flex',alignItems:'center',gap:8,color:C.text,flexShrink:0}}>
        <div style={{width:28,height:28,borderRadius:8,background:'linear-gradient(135deg,#0C69C8,#0a47a0)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13}}>🏃</div>
        <span>Snows<span style={{color:C.accent2}}>ProAm</span></span>
      </div>

      {/* Desktop nav */}
      <nav className="header-nav" style={{display:'flex',gap:4}}>
        {[['dashboard','Dashboard'],['schools','🏫 Ireland Schools'],['schools_uk','🇬🇧 UK Schools'],['padel','🎾 Padel']].map(([id,label])=>(
          <button key={id} onClick={()=>setPage(id)} className="nav-btn" style={{
            background:page===id?`${C.accent}15`:'none',
            border:`1px solid ${page===id?C.accent+'40':'transparent'}`,
            color:page===id?C.accent2:C.muted,
          }}>{label}</button>
        ))}
      </nav>

      {/* Desktop right */}
      <div className="header-right-desktop" style={{display:'flex',alignItems:'center',gap:8}}>
        <div style={{display:'flex',alignItems:'center'}}>
          {onlineUsers.slice(0,4).map((u,i)=>(
            <div key={u.user_id||i} title={u.name} style={{position:'relative',marginLeft:i>0?-6:0}}>
              <div style={{width:26,height:26,borderRadius:7,background:u.color||C.accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'#fff',fontWeight:700,border:`2px solid ${C.bg}`}}>{u.emoji||initials(u.name)}</div>
              <div style={{position:'absolute',bottom:-1,right:-1,width:7,height:7,borderRadius:'50%',background:C.green,border:`2px solid ${C.bg}`,animation:'pulse 1.6s ease-in-out infinite'}}/>
            </div>
          ))}
        </div>
        <button onClick={onFeed} style={{position:'relative',padding:'6px 12px',borderRadius:9,cursor:'pointer',background:`${C.accent}18`,border:`1px solid ${C.accent}35`,color:C.accent2,fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
          📋 Feed
          {unread>0&&<span style={{background:C.rose,color:'#fff',fontSize:9,fontWeight:800,padding:'1px 5px',borderRadius:20}}>{unread}</span>}
        </button>
        <ThemeToggle isDark={isDark} onToggle={onTheme}/>
        <div style={{position:'relative'}}>
          <div onClick={()=>setMenuOpen(m=>!m)} style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',padding:'4px 8px',borderRadius:9,border:`1px solid ${C.border2}`}}>
            <Avatar p={profile} size={24}/>
            <span style={{fontSize:12,fontWeight:600,color:C.text}}>{profile?.display_name}</span>
            <span style={{fontSize:11,color:C.muted}}>{menuOpen?'▲':'▼'}</span>
          </div>
          {menuOpen&&(
            <>
              <div onClick={()=>setMenuOpen(false)} style={{position:'fixed',inset:0,zIndex:200}}/>
              <div style={{position:'absolute',right:0,top:42,background:C.card,border:`1px solid ${C.border2}`,borderRadius:14,padding:8,minWidth:180,zIndex:201,boxShadow:'0 8px 32px rgba(0,0,0,.3)'}}>
                <button onClick={()=>{setMenuOpen(false);onEditProfile()}} style={{width:'100%',padding:'10px 14px',background:'none',border:'none',borderRadius:9,color:C.text,fontSize:13,textAlign:'left',cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>✏️ Edit Profile</button>
                <button onClick={()=>{setMenuOpen(false);onLock()}} style={{width:'100%',padding:'10px 14px',background:'none',border:'none',borderRadius:9,color:C.muted,fontSize:13,textAlign:'left',cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>🔒 Lock & Switch</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile right — just theme + feed + avatar menu */}
      <div className="header-right-mobile" style={{display:'none',alignItems:'center',gap:8}}>
        <button onClick={onFeed} style={{position:'relative',width:34,height:34,borderRadius:9,background:`${C.accent}18`,border:`1px solid ${C.accent}35`,color:C.accent2,fontSize:16,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
          📋
          {unread>0&&<span style={{position:'absolute',top:-3,right:-3,background:C.rose,color:'#fff',fontSize:8,fontWeight:800,padding:'1px 4px',borderRadius:20}}>{unread}</span>}
        </button>
        <ThemeToggle isDark={isDark} onToggle={onTheme}/>
        <div style={{position:'relative'}}>
          <div onClick={()=>setMenuOpen(m=>!m)} style={{display:'flex',alignItems:'center',cursor:'pointer'}}>
            <Avatar p={profile} size={32}/>
          </div>
          {menuOpen&&(
            <>
              <div onClick={()=>setMenuOpen(false)} style={{position:'fixed',inset:0,zIndex:200}}/>
              <div style={{position:'absolute',right:0,top:42,background:C.card,border:`1px solid ${C.border2}`,borderRadius:14,padding:8,minWidth:160,zIndex:201,boxShadow:'0 8px 32px rgba(0,0,0,.3)'}}>
                <div style={{padding:'8px 14px 6px',fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.4}}>{profile?.display_name}</div>
                <button onClick={()=>{setMenuOpen(false);onEditProfile()}} style={{width:'100%',padding:'10px 14px',background:'none',border:'none',borderRadius:9,color:C.text,fontSize:13,textAlign:'left',cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>✏️ Edit Profile</button>
                <button onClick={()=>{setMenuOpen(false);onLock()}} style={{width:'100%',padding:'10px 14px',background:'none',border:'none',borderRadius:9,color:C.muted,fontSize:13,textAlign:'left',cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>🔒 Lock & Switch</button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

/* ═══════════════════════════════════════════════════════════
   BOTTOM NAV (mobile)
═══════════════════════════════════════════════════════════ */
function BottomNav({ page, setPage, C }) {
  const items = [
    { id:'dashboard', icon:'📊', label:'Dashboard' },
    { id:'schools',   icon:'🏫', label:'Ireland' },
    { id:'schools_uk', icon:'🇬🇧', label:'UK' },
    { id:'padel',     icon:'🎾', label:'Padel' },
  ]
  return (
    <nav className="bottom-nav">
      {items.map(({id,icon,label})=>(
        <button key={id} className={`bottom-nav-btn${page===id?' active':''}`} onClick={()=>setPage(id)}>
          <span className="icon">{icon}</span>
          {label}
        </button>
      ))}
    </nav>
  )
}

/* ═══════════════════════════════════════════════════════════
   ACTIVITY DRAWER
═══════════════════════════════════════════════════════════ */
function ActivityDrawer({ open, onClose, activities, profiles, myId, C }) {
  const recent = activities.slice(0, 8)
  return (
    <>
      {open&&<div onClick={onClose} style={{position:'fixed',inset:0,zIndex:198,background:'rgba(0,0,0,.55)'}}/>}
      <div style={{position:'fixed',right:0,top:0,bottom:0,width:'min(360px,100vw)',background:C.surface,borderLeft:`1px solid ${C.border2}`,zIndex:199,transform:open?'translateX(0)':'translateX(100%)',transition:'transform .32s cubic-bezier(.16,1,.3,1)',display:'flex',flexDirection:'column'}}>
        <div style={{padding:'20px 16px 14px',borderBottom:`1px solid ${C.border2}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:800,color:C.text}}>Recent Activity</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>Last 8 calls — all agents</div>
          </div>
          <button onClick={onClose} style={{background:C.inputBg,border:`1px solid ${C.border2}`,color:C.muted,width:30,height:30,borderRadius:8,fontSize:16,cursor:'pointer'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'8px 0'}}>
          {recent.length===0 ? (
            <div style={{textAlign:'center',padding:'48px 20px',color:C.muted2,fontSize:13}}>
              <div style={{fontSize:36,marginBottom:12}}>📞</div>No calls logged yet.
            </div>
          ) : recent.map(a=>{
            const caller = profiles[a.user_id]
            const isMe = a.user_id===myId
            const sm = STATUS_MAP[a.status_to||'']
            return (
              <div key={a.id} style={{padding:'12px 16px',borderBottom:`1px solid rgba(255,255,255,.035)`,display:'flex',gap:10,alignItems:'flex-start'}}>
                <Avatar p={caller} size={32}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:3,lineHeight:1.4,color:C.text}}>
                    <span style={{color:isMe?C.accent2:(caller?.color||C.text)}}>{isMe?'You':(caller?.display_name||'Unknown')}</span>
                    <span style={{color:C.muted,fontWeight:400}}> → {a.pipeline==='schools'?'🏫':a.pipeline==='schools_uk'?'🇬🇧':'🎾'} </span>
                    <span>{a.entity_name}</span>
                  </div>
                  {a.status_to&&<span style={{display:'inline-block',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,background:sm?.bg||C.inputBg,color:sm?.fg||C.text,marginBottom:a.notes?4:0}}>{sm?.label||a.status_to}</span>}
                  {a.notes&&<div style={{fontSize:11,color:C.muted,fontStyle:'italic',marginTop:2,lineHeight:1.4}}>"{a.notes.length>70?a.notes.slice(0,70)+'…':a.notes}"</div>}
                  <div style={{fontSize:10,color:C.muted2,marginTop:4}}>{timeAgo(a.created_at)}</div>
                </div>
              </div>
            )
          })}
        </div>
        {activities.length > 8 && (
          <div style={{padding:'10px 16px',borderTop:`1px solid ${C.border2}`,fontSize:11,color:C.muted,textAlign:'center'}}>
            Showing 8 most recent · {activities.length} total logged
          </div>
        )}
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════
   LEAD MODAL — sheet on mobile, centred on desktop
═══════════════════════════════════════════════════════════ */
function LeadModal({ lead, pipeline, leadStates, profile, profiles, onSave, onClose, C }) {
  const key  = lead ? lk(pipeline, lead.id) : ''
  const st   = lead ? (leadStates[key]||{}) : {}
  const caller = (st.last_called_by && st.status) ? profiles[st.last_called_by] : null
  const [status, setStatus] = useState(st.status||'')
  const [notes,  setNotes]  = useState(st.notes||'')
  const isMobile = useIsMobile(700)

  useEffect(() => { setStatus(st.status||''); setNotes(st.notes||'') }, [lead?.id])

  if (!lead) return null
  const save = () => { onSave({ key, status, notes, entityName: lead.name, pipeline }); onClose() }
  const isPadel = pipeline === 'padel'
  const isUK = pipeline === 'schools_uk'

  const inner = (
    <div className="lead-modal-inner" style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:isMobile?'20px 20px 0 0':22,padding:isMobile?'20px 16px':28,width:'100%',maxWidth:isMobile?undefined:560,maxHeight:'92vh',overflowY:'auto',position:'relative',boxShadow:'0 0 80px rgba(12,105,200,.18)'}}>
      <button onClick={onClose} style={{position:'absolute',top:16,right:16,background:'none',border:'none',color:C.muted,fontSize:18,cursor:'pointer',width:32,height:32,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>

      {/* Drag handle on mobile */}
      {isMobile && <div style={{width:36,height:4,borderRadius:2,background:C.border2,margin:'0 auto 16px'}}/>}

      <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:800,marginBottom:4,paddingRight:36,color:C.text}}>{lead.name}</h2>
      <div style={{fontSize:12,color:C.muted,marginBottom:16}}>
        {isPadel
          ? `${lead.type} · ${lead.county} · ${lead.courts||'?'} courts`
          : `${lead.type||'Secondary School'} · ${lead.county} · ${lead.enrolment||'?'} students${lead.deis==='Y'?' · DEIS':''}${isUK && lead.sports_tag ? ` · ${lead.sports_tag}` : ''}`}
      </div>

      {caller&&(
        <div style={{background:`${caller.color||C.accent}12`,border:`1px solid ${caller.color||C.accent}25`,borderRadius:11,padding:'10px 12px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
          <Avatar p={caller} size={30}/>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:C.text}}>{st.last_called_by===profile?.id?'You':caller.display_name} last called</div>
            <div style={{fontSize:11,color:C.muted,marginTop:1}}>{timeAgo(st.last_called_at)} · {st.call_count||1} call{(st.call_count||1)!==1?'s':''}</div>
          </div>
        </div>
      )}

      <div className="lead-modal-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
        {isPadel ? <>
          <Field label="Courts" value={lead.courts||'?'} big color={C.green} C={C}/>
          <Field label="Type"   value={lead.type}        C={C}/>
          <Field label="Phone"  value={lead.phone}       isPhone C={C}/>
          <Field label="Email"  value={lead.email}       isEmail C={C}/>
        </> : <>
          <Field label="Enrolment" value={lead.enrolment} big color={C.accent2} C={C}/>
          <Field label="Principal" value={lead.principal} C={C}/>
          <Field label="Phone"     value={lead.phone}     isPhone C={C}/>
          <Field label="Email"     value={lead.email}     isEmail C={C}/>
          {isUK && <Field label="Second email" value={lead.email_2} isEmail C={C}/>}
        </>}
        <div style={{gridColumn:'1/-1'}}>
          <Field label="Address" value={lead.address ? `${lead.address}${lead.county?`, ${lead.county}`:''}${isUK && lead.postcode ? `, ${lead.postcode}` : ''}` : '—'} C={C}/>
        </div>
      </div>

      {isUK && <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
        <Field label="Aircall phone" value={lead.phone_aircall} isPhone C={C}/>
        <Field label="Region" value={lead.region} C={C}/>
        <Field label="URN" value={lead.urn} C={C}/>
        <Field label="Email review" value={lead.source_data?.['Email review status']} C={C}/>
        <Field label="Email 1 role" value={lead.source_data?.['Email 1 role']} C={C}/>
        <Field label="Email 2 role" value={lead.source_data?.['Email 2 role']} C={C}/>
        <Field label="Sports offered" value={lead.source_data?.['Sports offered']} C={C}/>
        <Field label="Sports offering stage" value={lead.source_data?.['Sports offering stage']} C={C}/>
        <Field label="Admissions" value={lead.source_data?.['Admissions policy']} C={C}/>
        <Field label="Email checked" value={lead.source_data?.['Email checked on']} C={C}/>
        {lead.website && <Field label="Website" value={lead.website} isLink C={C}/>}
        {lead.source_data?.['Contact page / form URL'] && <Field label="Contact page" value={lead.source_data['Contact page / form URL']} isLink C={C}/>}
        {lead.source_data?.['Email 1 source URL'] && <Field label="Email 1 source" value={lead.source_data['Email 1 source URL']} isLink C={C}/>}
        {lead.source_data?.['Email 2 source URL'] && <Field label="Email 2 source" value={lead.source_data['Email 2 source URL']} isLink C={C}/>}
        {lead.source_data?.['GIAS record URL'] && <Field label="GIAS record" value={lead.source_data['GIAS record URL']} isLink C={C}/>}
        {lead.source_data?.['Sports source URL'] && <Field label="Sports source" value={lead.source_data['Sports source URL']} isLink C={C}/>}
      </div>}

      {(lead.notes_pre||(lead.deis==='Y'))&&(
        <>
          <div style={{fontSize:10,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.7,marginBottom:6}}>💡 Pitch Angle</div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`3px solid ${C.accent}`,borderRadius:'0 10px 10px 0',padding:'10px 14px',fontSize:13,lineHeight:1.7,marginBottom:14,color:C.muted}}>
            {lead.notes_pre||(lead.deis==='Y'?`DEIS school — lead with student wellbeing and free platform angle. Target ${lead.principal||'PE coordinator or principal'}.`:'')}
          </div>
        </>
      )}

      <div style={{fontSize:10,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.7,marginBottom:7}}>📊 Status</div>
      <div className="lead-modal-status" style={{display:'flex',gap:7,flexWrap:'wrap',marginBottom:14}}>
        {Object.entries(STATUS_MAP).filter(([k])=>k!=='').map(([k,m])=>(
          <button key={k} onClick={()=>setStatus(k)} style={{background:status===k?m.bg:C.inputBg,color:status===k?m.fg:C.muted,border:`1px solid ${status===k?m.fg+'50':C.border2}`,borderRadius:8,padding:'7px 10px',fontSize:11,fontWeight:700,cursor:'pointer',transition:'all .15s'}}>{m.label}</button>
        ))}
      </div>

      <div style={{fontSize:10,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.7,marginBottom:7}}>📝 Your Notes</div>
      <textarea className="mnotes" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Call notes, follow-up date, objections handled…"/>

      <button onClick={save} style={{width:'100%',marginTop:12,padding:14,background:'linear-gradient(135deg,#0C69C8 0%,#0a47a0 100%)',border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer'}}>Save & Log Call →</button>
    </div>
  )

  if (isMobile) {
    return (
      <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:500,background:'rgba(0,0,0,.78)',backdropFilter:'blur(7px)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
        {inner}
      </div>
    )
  }

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:500,background:'rgba(0,0,0,.78)',backdropFilter:'blur(7px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      {inner}
    </div>
  )
}

function Field({ label, value, big, color, isPhone, isEmail, isLink, C }) {
  return (
    <div>
      <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.5,marginBottom:3}}>{label}</div>
      {big
        ? <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,color:color||C.text}}>{value||'—'}</div>
        : isPhone && value
          ? <a href={`tel:${value}`} style={{color:C.accent2,fontSize:13}}>{value}</a>
          : isEmail && value
            ? <a href={`mailto:${value}`} style={{color:C.accent2,fontSize:12,wordBreak:'break-all'}}>{value}</a>
            : isLink && value
              ? /^https?:\/\//i.test(value) ? <a href={value} target="_blank" rel="noopener noreferrer" style={{color:C.accent2,fontSize:12,overflowWrap:'anywhere'}}>{value}</a> : <div style={{fontSize:13,color:C.text}}>{value}</div>
            : <div style={{fontSize:13,color:C.text}}>{value||'—'}</div>
      }
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   LEAD FORM MODAL — bottom sheet on mobile
═══════════════════════════════════════════════════════════ */
function LeadFormModal({ pipeline, lead, onSave, onClose, onDelete, C }) {
  const isEdit = !!lead
  const isSchool = pipeline !== 'padel'
  const isUK = pipeline === 'schools_uk'
  const isMobile = useIsMobile(700)

  const blank = isSchool
    ? isUK
      ? { name:'', county:'', address:'', principal:'', email:'', email_2:'', phone:'', phone_aircall:'', enrolment:'', type:'Other state secondary', urn:'', region:'', postcode:'', sports_tag:'', website:'', notes_pre:'' }
      : { name:'', county:'Dublin', address:'', principal:'', email:'', phone:'', enrolment:'', type:'Secondary', deis:'N', gender:'Mixed', fee_paying:'N', roll_number:'', notes_pre:'' }
    : { name:'', county:'Dublin', address:'', phone:'', email:'', website:'', courts:'', type:'Indoor', notes_pre:'' }

  const [form, setForm] = useState(lead ? { ...lead } : blank)
  const [busy,  setBusy]  = useState(false)
  const [err,   setErr]   = useState('')
  const [confirmDel, setConfirmDel] = useState(false)

  const set = (k,v) => setForm(f=>({...f,[k]:v}))

  const submit = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return }
    setBusy(true); setErr('')
    const payload = { ...form, pipeline }
    if (isSchool) { payload.enrolment = parseInt(form.enrolment)||null }
    else          { payload.courts    = parseInt(form.courts)||0 }
    const { data, error } = isEdit
      ? await supabase.from('crm_pipeline_leads').update(payload).eq('id', lead.id).select().single()
      : await supabase.from('crm_pipeline_leads').insert(payload).select().single()
    if (error) { setErr(error.message); setBusy(false); return }
    onSave(data, isEdit); onClose()
  }

  const doDelete = async () => {
    setBusy(true)
    await supabase.from('crm_pipeline_leads').delete().eq('id', lead.id)
    onDelete(lead.id); onClose()
  }

  const lbl = txt => <label style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:.5,display:'block',marginBottom:6,textTransform:'uppercase'}}>{txt}</label>
  const inp = (k, placeholder, type='text') => <input type={type} value={form[k]||''} onChange={e=>set(k,e.target.value)} placeholder={placeholder} className="finp"/>
  const sel = (k, opts) => <select value={form[k]||''} onChange={e=>set(k,e.target.value)} className="fsel">{opts.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}</select>
  const counties = isSchool ? SCHOOL_COUNTIES : PADEL_COUNTIES

  const inner = (
    <div className="lform-inner" style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:isMobile?'20px 20px 0 0':22,padding:isMobile?'20px 16px':28,width:'100%',maxWidth:isMobile?undefined:600,maxHeight:'92vh',overflowY:'auto',position:'relative',boxShadow:'0 0 80px rgba(12,105,200,.2)'}}>
      <button onClick={onClose} style={{position:'absolute',top:16,right:16,background:'none',border:'none',color:C.muted,fontSize:18,cursor:'pointer',width:32,height:32,borderRadius:8}}>✕</button>
      {isMobile && <div style={{width:36,height:4,borderRadius:2,background:C.border2,margin:'0 auto 14px'}}/>}
      <div style={{marginBottom:18}}>
        <div style={{fontSize:11,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.7,marginBottom:4}}>{isEdit ? '✏️ Edit Lead' : '➕ Add New Lead'}</div>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:800,color:C.text,paddingRight:36}}>{isEdit ? lead.name : (isSchool ? 'New Secondary School' : 'New Padel Club')}</h2>
      </div>

      <div className="lform-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <div style={{gridColumn:'1/-1'}}>{lbl('Name *')}{inp('name', isSchool ? "e.g. St. Joseph's CBS" : 'e.g. House of Padel')}</div>
        <div>{lbl(isUK ? 'Local authority' : 'County')}{isUK ? inp('county', 'e.g. Birmingham') : sel('county', counties)}</div>
        <div>{lbl('Phone')}{inp('phone', isUK ? '020 1234 5678' : '+353 1 234 5678', 'tel')}</div>
        <div style={{gridColumn:'1/-1'}}>{lbl('Address')}{inp('address', 'Street address, town')}</div>
        <div style={{gridColumn:'1/-1'}}>{lbl('Email')}{inp('email', isUK ? 'office@school.org.uk' : 'contact@example.ie', 'email')}</div>

        {isUK && <>
          <div>{lbl('Second email')}{inp('email_2', 'head@example.org.uk', 'email')}</div>
          <div>{lbl('Aircall phone (+44)')}{inp('phone_aircall', '+442012345678', 'tel')}</div>
          <div>{lbl('URN')}{inp('urn', 'e.g. 136290')}</div>
          <div>{lbl('Region')}{inp('region', 'e.g. West Midlands')}</div>
          <div>{lbl('Postcode')}{inp('postcode', 'e.g. B32 4BT')}</div>
          <div>{lbl('School category')}{sel('type', ['Other state secondary','Private','Grammar (state)'])}</div>
          <div>{lbl('Sports tag')}{inp('sports_tag', 'e.g. Sports academy programme')}</div>
          <div>{lbl('Website')}{inp('website', 'https://school.org.uk', 'url')}</div>
        </>}

        {isSchool && !isUK ? <>
          <div>{lbl('Principal / Contact')}{inp('principal', 'e.g. Mr. Murphy')}</div>
          <div>{lbl('Enrolment')}{inp('enrolment', 'e.g. 850', 'number')}</div>
          <div>{lbl('School Type')}{sel('type', ['Secondary','Community','Comprehensive','Vocational','Fee-paying'])}</div>
          <div>{lbl('Gender')}{sel('gender', ['Mixed','Boys','Girls'])}</div>
          <div>{lbl('DEIS School?')}{sel('deis', [{value:'N',label:'No'},{value:'Y',label:'Yes — DEIS'}])}</div>
          <div>{lbl('Fee Paying?')}{sel('fee_paying', [{value:'N',label:'No'},{value:'Y',label:'Yes'}])}</div>
          <div style={{gridColumn:'1/-1'}}>{lbl('Roll Number')}{inp('roll_number', 'e.g. 70060B')}</div>
        </> : !isSchool ? <>
          <div>{lbl('Website')}{inp('website', 'houseofpadel.co')}</div>
          <div>{lbl('Number of Courts')}{inp('courts', 'e.g. 4', 'number')}</div>
          <div style={{gridColumn:'1/-1'}}>{lbl('Court Type')}{sel('type', ['Indoor','Outdoor','Hotel','Mixed'])}</div>
        </> : <>
          <div>{lbl('Headteacher / principal')}{inp('principal', 'Name')}</div>
          <div>{lbl('Number of pupils')}{inp('enrolment', 'e.g. 850', 'number')}</div>
        </>}

        <div style={{gridColumn:'1/-1'}}>
          {lbl('Pitch Angle / Pre-call Notes')}
          <textarea value={form.notes_pre||''} onChange={e=>set('notes_pre',e.target.value)} className="mnotes" placeholder="Key pitch angle, best approach…"/>
        </div>
      </div>

      {err&&<div style={{fontSize:12,color:C.rose,marginTop:10,fontWeight:600}}>{err}</div>}
      <div className="lform-btns" style={{display:'flex',gap:10,marginTop:16}}>
        {isEdit && !confirmDel && <button onClick={()=>setConfirmDel(true)} style={{padding:'12px 14px',background:'transparent',border:`1px solid ${C.rose}50`,borderRadius:11,color:C.rose,fontSize:13,fontWeight:600,cursor:'pointer',flexShrink:0}}>🗑 Delete</button>}
        {isEdit && confirmDel && <button onClick={doDelete} disabled={busy} style={{padding:'12px 14px',background:C.rose,border:'none',borderRadius:11,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',flexShrink:0}}>{busy ? '…' : 'Confirm Delete'}</button>}
        <button onClick={onClose} style={{flex:1,padding:12,background:'none',border:`1px solid ${C.border2}`,borderRadius:11,color:C.muted,fontSize:13,cursor:'pointer'}}>Cancel</button>
        <button onClick={submit} disabled={busy} style={{flex:2,padding:12,background:'linear-gradient(135deg,#0C69C8,#0a47a0)',border:'none',borderRadius:11,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>{busy ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Lead'}</button>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:600,background:'rgba(0,0,0,.82)',backdropFilter:'blur(8px)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
        {inner}
      </div>
    )
  }

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:600,background:'rgba(0,0,0,.82)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      {inner}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   MOBILE LEAD CARD
   FIX: Edit button is now inline beside the status button,
        not absolutely positioned on top of it.
═══════════════════════════════════════════════════════════ */
function LeadCard({ lead, pipeline, leadState, profiles, myId, onRowClick, onQuickStatus, onEdit, C }) {
  const st = leadState || {}
  const caller = st.last_called_by && st.status ? profiles[st.last_called_by] : null
  const isPadel = pipeline === 'padel'
  const isUK = pipeline === 'schools_uk'

  return (
    <div className="lead-card" onClick={() => onRowClick(lead, pipeline)}>
      {/* ── Top row: name + [status btn] [edit btn] ── */}
      <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:10}}>
        {/* Name / subtitle */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lead.name}</div>
          <div style={{fontSize:12,color:C.muted}}>{lead.county} {isPadel ? `· ${lead.courts||'?'} courts` : `· ${lead.enrolment||'?'} students`}</div>
        </div>

        {/* ── Status + Edit side by side — NO absolute positioning ── */}
        <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
          <StatusBtn
            status={st.status}
            onClick={() => onQuickStatus(lead, pipeline, STATUS_CYCLE[(STATUS_CYCLE.indexOf(st.status||'')+1) % STATUS_CYCLE.length])}
            C={C}
          />
          <button
            onClick={e => { e.stopPropagation(); onEdit(lead) }}
            style={{
              background: C.inputBg,
              border: `1px solid ${C.border2}`,
              borderRadius: 7,
              padding: '5px 8px',
              fontSize: 13,
              color: C.muted,
              cursor: 'pointer',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >✏️</button>
        </div>
      </div>

      {/* ── Bottom row: tags + caller ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          {lead.phone && (
            <a href={`tel:${lead.phone}`} onClick={e=>e.stopPropagation()} style={{color:C.accent2,fontSize:12,display:'flex',alignItems:'center',gap:4}}>
              📞 {lead.phone}
            </a>
          )}
          {!isPadel && !isUK && lead.deis==='Y' && (
            <span style={{display:'inline-flex',alignItems:'center',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700,background:'rgba(251,191,36,.12)',color:C.amber,border:'1px solid rgba(251,191,36,.25)'}}>DEIS</span>
          )}
          {isUK && <span style={{fontSize:10,color:C.muted}}>{lead.type}{lead.sports_tag ? ` · ${lead.sports_tag}` : ''}</span>}
          {isPadel && (
            <span style={{display:'inline-flex',alignItems:'center',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700,background:'rgba(167,139,250,.12)',color:'#A78BFA',border:'1px solid rgba(167,139,250,.22)'}}>{lead.type}</span>
          )}
        </div>
        {caller && <CallerTag state={st} profiles={profiles} myId={myId} C={C}/>}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   DASHBOARD PAGE
═══════════════════════════════════════════════════════════ */
function DashboardPage({ leadStates, profiles, schoolCount, ukSchoolCount, padelCount, C }) {
  const stats = useMemo(()=>{
    const vals = Object.values(leadStates)
    const called     = vals.filter(s=>s.status).length
    const interested = vals.filter(s=>s.status==='s-interested').length
    const booked     = vals.filter(s=>s.status==='s-booked').length
    const scCalled   = Object.entries(leadStates).filter(([k,v])=>k.startsWith('sc_')&&v.status).length
    const ukCalled   = Object.entries(leadStates).filter(([k,v])=>k.startsWith('uk_')&&v.status).length
    const pdCalled   = Object.entries(leadStates).filter(([k,v])=>k.startsWith('pd_')&&v.status).length
    const board = {}
    vals.forEach(s=>{ if (!s.last_called_by || !s.status) return; board[s.last_called_by]=(board[s.last_called_by]||0)+1 })
    return { called, interested, booked, scCalled, ukCalled, pdCalled, board }
  }, [leadStates])

  const statCards = [
    { label:'Total Leads',  val:schoolCount+ukSchoolCount+padelCount,  sub:`${schoolCount} Ireland · ${ukSchoolCount} UK · ${padelCount} padel`, color:C.accent,  icon:'📊' },
    { label:'Contacted',    val:stats.called,             sub:'all pipelines',              color:C.accent2, icon:'📞' },
    { label:'Interested',   val:stats.interested,         sub:'warm leads',                  color:C.amber,   icon:'⭐' },
    { label:'Demos Booked', val:stats.booked,             sub:'scheduled',                   color:C.green,   icon:'📅' },
  ]

  return (
    <div className="page-wrap" style={{maxWidth:1320,margin:'0 auto',padding:'28px 16px 80px'}}>
      <div className="fu dash-hero" style={{marginBottom:20,padding:'28px 24px',background:C.card,border:`1px solid ${C.border}`,borderRadius:20,position:'relative',overflow:'hidden',boxShadow:'0 4px 32px rgba(12,105,200,.08)'}}>
        <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 0% 50%,rgba(12,105,200,.08) 0%,transparent 60%)',pointerEvents:'none'}}/>
        <div style={{position:'relative'}}>
          <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:'clamp(18px,4vw,28px)',fontWeight:900,letterSpacing:-1,marginBottom:6,lineHeight:1.1,color:C.text}}>
            Project <span style={{background:'linear-gradient(135deg,#38BDF8,#0C69C8)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>Athlete</span>
          </h1>
          <p style={{fontSize:13,color:C.muted,lineHeight:1.5,maxWidth:500,marginBottom:14}}>Your real-time shared CRM. Every call synced instantly.</p>
          <div className="dash-tags" style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {[`🏫 ${schoolCount} Ireland schools`,`🇬🇧 ${ukSchoolCount} UK schools`,`🎾 ${padelCount} Clubs`,`📞 ${stats.scCalled} Ireland called`,`📞 ${stats.ukCalled} UK called`,`🎾 ${stats.pdCalled} Clubs called`].map(t=>(
              <div key={t} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:8,background:C.surface,border:`1px solid ${C.border2}`,fontSize:11,fontWeight:600,color:C.text}}>{t}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="fu2 stat-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {statCards.map(s=>(
          <div key={s.label} className="stat-card" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:16,padding:'16px 14px',position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',top:-20,right:-20,width:80,height:80,borderRadius:'50%',background:`${s.color}18`,filter:'blur(20px)',pointerEvents:'none'}}/>
            <div style={{width:34,height:34,borderRadius:9,background:`${s.color}15`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,marginBottom:10}}>{s.icon}</div>
            <div style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:.5,marginBottom:4}}>{s.label}</div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:'clamp(18px,3vw,26px)',fontWeight:800,color:C.text}}>{s.val}</div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="fu3 dash-bottom-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <div style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:18,padding:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.purple,textTransform:'uppercase',letterSpacing:.6,marginBottom:12}}>🏆 Team Leaderboard</div>
          {Object.entries(stats.board).length===0 ? (
            <div style={{fontSize:13,color:C.muted2}}>No calls yet — start dialling!</div>
          ) : Object.entries(stats.board).sort(([,a],[,b])=>b-a).map(([uid,count],i)=>{
            const p = profiles[uid]
            return (
              <div key={uid} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<Object.keys(stats.board).length-1?`1px solid ${C.border2}`:'none'}}>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,color:i===0?C.amber:C.muted,width:20,textAlign:'center'}}>{i+1}</div>
                <Avatar p={p} size={32}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p?.display_name||uid}</div>
                  <div style={{fontSize:11,color:C.muted}}>{count} calls</div>
                </div>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,color:i===0?C.amber:C.muted}}>{count}</div>
              </div>
            )
          })}
        </div>

        <div style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:18,padding:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:'uppercase',letterSpacing:.6,marginBottom:12}}>🚀 Roadmap</div>
          {[
            {done:true, label:'CRM built & deployed'},
            {done:false,label:'Secondary Schools outreach (Apr–Jun)'},
            {done:false,label:'Padel club pilots (May–Jul)'},
            {done:false,label:'School Challenge LIVE (Sept)'},
            {done:false,label:'50+ clubs on ProAm Map'},
          ].map((m,i)=>(
            <div key={i} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'7px 0',borderBottom:i<4?`1px solid ${C.border2}`:'none'}}>
              <span style={{fontSize:13,color:m.done?C.green:C.muted2,flexShrink:0,marginTop:1}}>{m.done?'✓':'○'}</span>
              <span style={{fontSize:12,color:m.done?C.text:C.muted,lineHeight:1.4}}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   SCHOOLS PAGE
═══════════════════════════════════════════════════════════ */
function SchoolsPage({ schools, setSchools, leadStates, profile, profiles, onRowClick, onQuickStatus, C }) {
  const [search, setSearch] = useState('')
  const [county, setCounty] = useState('')
  const [sf,     setSf]     = useState('')
  const [deis,   setDeis]   = useState(false)
  const [sort,   setSort]   = useState({col:'enrolment',dir:-1})
  const [addOpen, setAddOpen] = useState(false)
  const [editLead, setEditLead] = useState(null)
  const isMobile = useIsMobile(900)

  const counties = useMemo(()=>[...new Set(schools.map(s=>s.county))].sort(),[schools])
  const rows = useMemo(()=>{
    let d = [...schools]
    if (search){const q=search.toLowerCase();d=d.filter(r=>[r.name,r.principal,r.address,r.county,r.phone,r.email].join(' ').toLowerCase().includes(q))}
    if (county) d=d.filter(r=>r.county===county)
    if (deis)   d=d.filter(r=>r.deis==='Y')
    if (sf) d=d.filter(r=>{ const s=(leadStates[lk('schools',r.id)]||{}).status||''; return sf==='uncalled'?!s:s===sf })
    d.sort((a,b)=>{
      let av=a[sort.col]||'',bv=b[sort.col]||''
      if(sort.col==='enrolment'){av=+av;bv=+bv}else{av=String(av).toLowerCase();bv=String(bv).toLowerCase()}
      return av<bv?sort.dir:av>bv?-sort.dir:0
    })
    return d
  },[search,county,sf,deis,sort,leadStates,schools])

  const th=(label,col)=><th onClick={()=>setSort(s=>({col,dir:s.col===col?-s.dir:-1}))} style={{color:sort.col===col?C.accent2:undefined}}>{label}{sort.col===col?(sort.dir===-1?' ↓':' ↑'):''}</th>
  const handleSaved = (lead, isEdit) => { if (isEdit) setSchools(prev=>prev.map(s=>s.id===lead.id?lead:s)); else setSchools(prev=>[...prev,lead]) }
  const handleDeleted = (id) => setSchools(prev=>prev.filter(s=>s.id!==id))

  return (
    <div className="page-wrap" style={{maxWidth:1320,margin:'0 auto',padding:'24px 14px 80px'}}>
      <div className="page-header" style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.accent2,textTransform:'uppercase',letterSpacing:.6,marginBottom:4}}>🏫 Pipeline</div>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:'clamp(16px,3vw,22px)',fontWeight:800,letterSpacing:-.5,marginBottom:4,color:C.text}}>Irish Secondary Schools</h2>
          <p style={{fontSize:12,color:C.muted,maxWidth:500,lineHeight:1.5}}>Target PE coordinators, sports teachers and principals.</p>
        </div>
        <button className="add-btn" onClick={()=>setAddOpen(true)} style={{padding:'10px 16px',background:'linear-gradient(135deg,#0C69C8,#0a47a0)',border:'none',borderRadius:11,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:7,flexShrink:0}}>➕ Add School</button>
      </div>

      {/* Filters */}
      <div className="filter-row" style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
        <div className="search-wrap" style={{position:'relative',flex:1,minWidth:180}}>
          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:14,pointerEvents:'none'}}>🔍</span>
          <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search school…" style={{width:'100%',background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px 9px 32px',color:C.text,fontSize:13,outline:'none'}}/>
        </div>
        <select value={county} onChange={e=>setCounty(e.target.value)} style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px',color:C.text,fontSize:13,outline:'none',cursor:'pointer'}}>
          <option value=''>All Counties</option>{counties.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sf} onChange={e=>setSf(e.target.value)} style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px',color:C.text,fontSize:13,outline:'none',cursor:'pointer'}}>
          {['All Statuses','uncalled','s-done','s-callback','s-interested','s-booked','s-no'].map(o=><option key={o} value={o==='All Statuses'?'':o}>{o==='All Statuses'?'All Statuses':STATUS_MAP[o]?.label||o}</option>)}
        </select>
        {!isMobile && <label style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:C.muted,cursor:'pointer',whiteSpace:'nowrap'}}>
          <input type="checkbox" checked={deis} onChange={e=>setDeis(e.target.checked)} style={{accentColor:C.amber}}/> DEIS only
        </label>}
        <div style={{marginLeft:'auto',fontSize:12,color:C.muted,padding:'5px 10px',borderRadius:20,background:C.card,border:`1px solid ${C.border2}`,whiteSpace:'nowrap'}}>
          <span style={{color:C.accent2,fontWeight:700}}>{rows.length}</span> schools
        </div>
      </div>

      {/* Desktop table */}
      <div className="tbl-wrap" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:18,overflow:'hidden'}}>
        <table className="tbl">
          <thead><tr>{th('School','name')}{th('County','county')}{th('Students','enrolment')}<th>Principal</th><th>Contact</th><th>Flags</th><th>Last Called By</th><th>Status</th><th>Edit</th></tr></thead>
          <tbody>
            {rows.length===0&&<tr><td colSpan={9} style={{textAlign:'center',padding:'40px 20px',color:C.muted2}}><div style={{fontSize:28,marginBottom:10}}>🔍</div>No schools match your filters</td></tr>}
            {rows.map(r=>{
              const key=lk('schools',r.id); const st=leadStates[key]||{}
              return (
                <tr key={r.id} onClick={()=>onRowClick(r,'schools')}>
                  <td><div style={{fontWeight:600,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:C.text}} title={r.name}>{r.name}</div></td>
                  <td style={{color:C.muted,fontSize:12}}>{r.county}</td>
                  <td style={{fontFamily:"'Sora',sans-serif",fontWeight:700,color:r.enrolment>=1000?C.accent2:r.enrolment>=600?C.text:C.muted}}>{r.enrolment||'—'}</td>
                  <td style={{fontSize:12,color:C.muted,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.principal||'—'}</td>
                  <td><div style={{display:'flex',flexDirection:'column',gap:2}}>
                    {r.phone&&<a href={`tel:${r.phone}`} onClick={e=>e.stopPropagation()} style={{color:C.accent2,fontSize:12}}>{r.phone}</a>}
                    {r.email&&<a href={`mailto:${r.email}`} onClick={e=>e.stopPropagation()} style={{color:C.muted,fontSize:11}}>{r.email.length>22?r.email.slice(0,22)+'…':r.email}</a>}
                  </div></td>
                  <td>{r.deis==='Y'&&<span style={{display:'inline-flex',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700,background:'rgba(251,191,36,.12)',color:C.amber,border:'1px solid rgba(251,191,36,.25)'}}>DEIS</span>}</td>
                  <td><CallerTag state={st} profiles={profiles} myId={profile?.id} C={C}/></td>
                  <td onClick={e=>e.stopPropagation()}><StatusBtn status={st.status} onClick={()=>onQuickStatus(r,'schools',STATUS_CYCLE[(STATUS_CYCLE.indexOf(st.status||'')+1)%STATUS_CYCLE.length])} C={C}/></td>
                  <td onClick={e=>{e.stopPropagation();setEditLead(r)}}><button style={{background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:'5px 9px',fontSize:12,color:C.muted,cursor:'pointer'}}>✏️</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — onEdit passed as prop, no wrapper div needed */}
      <div className="mobile-cards" style={{display:'none'}}>
        {rows.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:C.muted2}}><div style={{fontSize:28,marginBottom:10}}>🔍</div>No schools match your filters</div>}
        {rows.map(r=>(
          <LeadCard
            key={r.id}
            lead={r}
            pipeline="schools"
            leadState={leadStates[lk('schools',r.id)]}
            profiles={profiles}
            myId={profile?.id}
            onRowClick={onRowClick}
            onQuickStatus={onQuickStatus}
            onEdit={r => setEditLead(r)}
            C={C}
          />
        ))}
      </div>

      {addOpen && <LeadFormModal pipeline="schools" onSave={handleSaved} onClose={()=>setAddOpen(false)} onDelete={handleDeleted} C={C}/>}
      {editLead && <LeadFormModal pipeline="schools" lead={editLead} onSave={handleSaved} onClose={()=>setEditLead(null)} onDelete={handleDeleted} C={C}/>}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   UK SCHOOLS PAGE — England records in the UK pipeline
═══════════════════════════════════════════════════════════ */
function UKSchoolsPage({ schools, setSchools, leadStates, profile, profiles, onRowClick, onQuickStatus, C }) {
  const [search, setSearch] = useState('')
  const [region, setRegion] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState('')
  const [sportsOnly, setSportsOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [addOpen, setAddOpen] = useState(false)
  const [editLead, setEditLead] = useState(null)
  const regions = useMemo(() => [...new Set(schools.map(s => s.region).filter(Boolean))].sort(), [schools])
  const rows = useMemo(() => schools.filter(s => {
    const q = search.trim().toLowerCase()
    if (q && ![s.name,s.urn,s.county,s.region,s.postcode,s.email,s.email_2,s.principal].some(v => String(v || '').toLowerCase().includes(q))) return false
    if (region && s.region !== region) return false
    if (category && s.type !== category) return false
    if (sportsOnly && !s.sports_tag) return false
    const leadStatus = leadStates[lk('schools_uk', s.id)]?.status || ''
    if (status && (status === 'uncalled' ? !!leadStatus : leadStatus !== status)) return false
    return true
  }).sort((a,b) => a.name.localeCompare(b.name)), [schools, search, region, category, status, sportsOnly, leadStates])
  const pageSize = 100
  const pages = Math.max(1, Math.ceil(rows.length / pageSize))
  const shown = rows.slice((Math.min(page, pages) - 1) * pageSize, Math.min(page, pages) * pageSize)
  const resetPage = setter => event => { setter(event.target.value); setPage(1) }
  const handleSaved = (lead, isEdit) => setSchools(prev => isEdit ? prev.map(s => s.id === lead.id ? lead : s) : [...prev, lead])
  const handleDeleted = id => setSchools(prev => prev.filter(s => s.id !== id))

  return <div className="page-wrap" style={{maxWidth:1320,margin:'0 auto',padding:'24px 14px 80px'}}>
    <div className="page-header" style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:12,marginBottom:16}}>
      <div>
        <div style={{fontSize:11,fontWeight:700,color:C.accent2,textTransform:'uppercase'}}>🇬🇧 UK pipeline · England schools</div>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:22,color:C.text,margin:'4px 0'}}>Secondary School Prospects</h2>
        <p style={{fontSize:12,color:C.muted}}>Use the filters to work through schools. Open a record for contacts, sources, notes and call status.</p>
      </div>
      <button className="add-btn" onClick={()=>setAddOpen(true)} style={{padding:'10px 16px',background:C.accent,border:0,borderRadius:10,color:'#fff',fontWeight:700}}>＋ Add School</button>
    </div>
    <div className="filter-row" style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:12}}>
      <input aria-label="Search UK schools" className="finp" style={{width:'min(100%,300px)'}} value={search} onChange={resetPage(setSearch)} placeholder="Search name, URN, postcode, email…"/>
      <select aria-label="Region" className="fsel" style={{width:'auto',minWidth:160}} value={region} onChange={resetPage(setRegion)}><option value="">All regions</option>{regions.map(r=><option key={r}>{r}</option>)}</select>
      <select aria-label="School category" className="fsel" style={{width:'auto',minWidth:160}} value={category} onChange={resetPage(setCategory)}><option value="">All categories</option>{['Other state secondary','Private','Grammar (state)'].map(c=><option key={c}>{c}</option>)}</select>
      <select aria-label="Call status" className="fsel" style={{width:'auto',minWidth:145}} value={status} onChange={resetPage(setStatus)}><option value="">All statuses</option><option value="uncalled">Not called</option>{Object.entries(STATUS_MAP).filter(([k])=>k).map(([k,v])=><option value={k} key={k}>{v.label}</option>)}</select>
      <label style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:C.muted}}><input type="checkbox" checked={sportsOnly} onChange={e=>{setSportsOnly(e.target.checked);setPage(1)}}/> Sports tagged only</label>
      <div aria-live="polite" style={{marginLeft:'auto',fontSize:12,color:C.muted}}>{rows.length.toLocaleString()} of {schools.length.toLocaleString()} schools</div>
    </div>
    <div className="desktop-table" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:14,overflow:'auto'}}>
      <table className="tbl"><thead><tr><th>School</th><th>Category</th><th>Area</th><th>Contacts</th><th>Sport</th><th>Last called by</th><th>Status</th><th>Edit</th></tr></thead><tbody>
        {shown.length === 0 && <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:C.muted}}>No schools match these filters.</td></tr>}
        {shown.map(s=>{const st=leadStates[lk('schools_uk',s.id)]||{};return <tr key={s.id} onClick={()=>onRowClick(s,'schools_uk')}>
          <td><strong>{s.name}</strong><div style={{fontSize:11,color:C.muted}}>URN {s.urn || '—'}</div></td>
          <td>{s.type || '—'}</td><td>{s.county || s.region || '—'}<div style={{fontSize:11,color:C.muted}}>{s.postcode}</div></td>
          <td><div>{s.email ? <a href={`mailto:${s.email}`} onClick={e=>e.stopPropagation()} style={{color:C.accent2}}>{s.email}</a> : '—'}</div>{s.email_2 && <div style={{fontSize:11}}><a href={`mailto:${s.email_2}`} onClick={e=>e.stopPropagation()}>{s.email_2}</a></div>}</td>
          <td style={{fontSize:11}}>{s.sports_tag || '—'}</td><td><CallerTag state={st} profiles={profiles} myId={profile?.id} C={C}/></td>
          <td onClick={e=>e.stopPropagation()}><StatusBtn status={st.status} onClick={()=>onQuickStatus(s,'schools_uk',STATUS_CYCLE[(STATUS_CYCLE.indexOf(st.status||'')+1)%STATUS_CYCLE.length])} C={C}/></td>
          <td onClick={e=>e.stopPropagation()}><button aria-label={`Edit ${s.name}`} onClick={()=>setEditLead(s)} style={{background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:'5px 9px',color:C.text}}>✏️</button></td>
        </tr>})}
      </tbody></table>
    </div>
    <div className="mobile-cards" style={{display:'none'}}>{shown.length === 0 && <p style={{padding:24,color:C.muted}}>No schools match these filters.</p>}{shown.map(s=><LeadCard key={s.id} lead={s} pipeline="schools_uk" leadState={leadStates[lk('schools_uk',s.id)]} profiles={profiles} myId={profile?.id} onRowClick={onRowClick} onQuickStatus={onQuickStatus} onEdit={setEditLead} C={C}/>)}</div>
    {pages > 1 && <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:12,marginTop:16}}><button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>Previous</button><span style={{fontSize:12,color:C.muted}}>Page {Math.min(page,pages)} of {pages}</span><button disabled={page>=pages} onClick={()=>setPage(p=>Math.min(pages,p+1))}>Next</button></div>}
    {addOpen && <LeadFormModal pipeline="schools_uk" onSave={handleSaved} onClose={()=>setAddOpen(false)} onDelete={handleDeleted} C={C}/>}
    {editLead && <LeadFormModal pipeline="schools_uk" lead={editLead} onSave={handleSaved} onClose={()=>setEditLead(null)} onDelete={handleDeleted} C={C}/>}
  </div>
}

/* ═══════════════════════════════════════════════════════════
   PADEL PAGE
═══════════════════════════════════════════════════════════ */
function PadelPage({ padel, setPadel, leadStates, profile, profiles, onRowClick, onQuickStatus, C }) {
  const [search, setSearch] = useState('')
  const [county, setCounty] = useState('')
  const [type,   setType]   = useState('')
  const [sf,     setSf]     = useState('')
  const [sort,   setSort]   = useState({col:'courts',dir:-1})
  const [addOpen, setAddOpen] = useState(false)
  const [editLead, setEditLead] = useState(null)
  const isMobile = useIsMobile(900)

  const counties = useMemo(()=>[...new Set(padel.map(p=>p.county))].sort(),[padel])
  const rows = useMemo(()=>{
    let d = [...padel]
    if (search){const q=search.toLowerCase();d=d.filter(r=>[r.name,r.address,r.county].join(' ').toLowerCase().includes(q))}
    if (county) d=d.filter(r=>r.county===county)
    if (type)   d=d.filter(r=>r.type===type)
    if (sf) d=d.filter(r=>{ const s=(leadStates[lk('padel',r.id)]||{}).status||''; return sf==='uncalled'?!s:s===sf })
    d.sort((a,b)=>{
      let av=a[sort.col]||'',bv=b[sort.col]||''
      if(sort.col==='courts'){av=+av;bv=+bv}else{av=String(av).toLowerCase();bv=String(bv).toLowerCase()}
      return av<bv?sort.dir:av>bv?-sort.dir:0
    })
    return d
  },[search,county,type,sf,sort,leadStates,padel])

  const th=(label,col)=><th onClick={()=>setSort(s=>({col,dir:s.col===col?-s.dir:-1}))} style={{color:sort.col===col?C.accent2:undefined}}>{label}{sort.col===col?(sort.dir===-1?' ↓':' ↑'):''}</th>
  const typeBadge=t=>{
    const m={Indoor:{bg:'rgba(167,139,250,.12)',fg:'#A78BFA',bo:'rgba(167,139,250,.22)'},Outdoor:{bg:'rgba(52,211,153,.08)',fg:'#6ee7b7',bo:'rgba(52,211,153,.15)'},Hotel:{bg:'rgba(251,191,36,.1)',fg:C.amber,bo:'rgba(251,191,36,.2)'},Mixed:{bg:'rgba(56,189,248,.1)',fg:C.accent2,bo:'rgba(56,189,248,.2)'}}
    const x=m[t]||m.Outdoor
    return <span style={{display:'inline-flex',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700,background:x.bg,color:x.fg,border:`1px solid ${x.bo}`}}>{t}</span>
  }
  const handleSaved = (lead, isEdit) => { if (isEdit) setPadel(prev=>prev.map(p=>p.id===lead.id?lead:p)); else setPadel(prev=>[...prev,lead]) }
  const handleDeleted = (id) => setPadel(prev=>prev.filter(p=>p.id!==id))

  return (
    <div className="page-wrap" style={{maxWidth:1320,margin:'0 auto',padding:'24px 14px 80px'}}>
      <div className="page-header" style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:'uppercase',letterSpacing:.6,marginBottom:4}}>🎾 Pipeline</div>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:'clamp(16px,3vw,22px)',fontWeight:800,letterSpacing:-.5,marginBottom:4,color:C.text}}>Irish Padel Clubs</h2>
          <p style={{fontSize:12,color:C.muted,maxWidth:500,lineHeight:1.5}}>Target club owners and managers. Lead with player retention and court fill rates.</p>
        </div>
        <button className="add-btn" onClick={()=>setAddOpen(true)} style={{padding:'10px 16px',background:`linear-gradient(135deg,${C.green}cc,${C.green}88)`,border:'none',borderRadius:11,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:7,flexShrink:0}}>➕ Add Club</button>
      </div>

      <div className="filter-row" style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
        <div className="search-wrap" style={{position:'relative',flex:1,minWidth:180}}>
          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:14,pointerEvents:'none'}}>🔍</span>
          <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search club…" style={{width:'100%',background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px 9px 32px',color:C.text,fontSize:13,outline:'none'}}/>
        </div>
        <select value={county} onChange={e=>setCounty(e.target.value)} style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px',color:C.text,fontSize:13,outline:'none',cursor:'pointer'}}>
          <option value=''>All Counties</option>{counties.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select value={type} onChange={e=>setType(e.target.value)} style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px',color:C.text,fontSize:13,outline:'none',cursor:'pointer'}}>
          <option value=''>All Types</option>{['Indoor','Outdoor','Hotel','Mixed'].map(t=><option key={t}>{t}</option>)}
        </select>
        <select value={sf} onChange={e=>setSf(e.target.value)} style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:10,padding:'9px 10px',color:C.text,fontSize:13,outline:'none',cursor:'pointer'}}>
          {['All Statuses','uncalled','s-done','s-callback','s-interested','s-booked','s-no'].map(o=><option key={o} value={o==='All Statuses'?'':o}>{o==='All Statuses'?'All Statuses':STATUS_MAP[o]?.label||o}</option>)}
        </select>
        <div style={{marginLeft:'auto',fontSize:12,color:C.muted,padding:'5px 10px',borderRadius:20,background:C.card,border:`1px solid ${C.border2}`,whiteSpace:'nowrap'}}>
          <span style={{color:C.green,fontWeight:700}}>{rows.length}</span> clubs
        </div>
      </div>

      {/* Desktop table */}
      <div className="tbl-wrap" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:18,overflow:'hidden'}}>
        <table className="tbl">
          <thead><tr>{th('Club','name')}{th('County','county')}{th('Courts','courts')}<th>Contact</th><th>Type</th><th>Last Called By</th><th>Status</th><th>Edit</th></tr></thead>
          <tbody>
            {rows.length===0&&<tr><td colSpan={8} style={{textAlign:'center',padding:'40px 20px',color:C.muted2}}><div style={{fontSize:28,marginBottom:10}}>🎾</div>No clubs match</td></tr>}
            {rows.map(r=>{
              const key=lk('padel',r.id); const st=leadStates[key]||{}
              return (
                <tr key={r.id} onClick={()=>onRowClick(r,'padel')}>
                  <td><div style={{fontWeight:600,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:C.text}} title={r.name}>{r.name}</div></td>
                  <td style={{color:C.muted,fontSize:12}}>{r.county}</td>
                  <td style={{fontFamily:"'Sora',sans-serif",fontWeight:700,color:C.text}}>{r.courts||'?'}</td>
                  <td><div style={{display:'flex',flexDirection:'column',gap:2}}>
                    {r.phone&&<a href={`tel:${r.phone}`} onClick={e=>e.stopPropagation()} style={{color:C.accent2,fontSize:12}}>{r.phone}</a>}
                    {r.email&&<a href={`mailto:${r.email}`} onClick={e=>e.stopPropagation()} style={{color:C.muted,fontSize:11}}>{r.email}</a>}
                    {r.website&&<a href={`https://${r.website}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{color:C.muted2,fontSize:11}}>{r.website}</a>}
                  </div></td>
                  <td>{typeBadge(r.type)}</td>
                  <td><CallerTag state={st} profiles={profiles} myId={profile?.id} C={C}/></td>
                  <td onClick={e=>e.stopPropagation()}><StatusBtn status={st.status} onClick={()=>onQuickStatus(r,'padel',STATUS_CYCLE[(STATUS_CYCLE.indexOf(st.status||'')+1)%STATUS_CYCLE.length])} C={C}/></td>
                  <td onClick={e=>{e.stopPropagation();setEditLead(r)}}><button style={{background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:'5px 9px',fontSize:12,color:C.muted,cursor:'pointer'}}>✏️</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — onEdit passed as prop, no wrapper div needed */}
      <div className="mobile-cards" style={{display:'none'}}>
        {rows.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:C.muted2}}><div style={{fontSize:28,marginBottom:10}}>🎾</div>No clubs match</div>}
        {rows.map(r=>(
          <LeadCard
            key={r.id}
            lead={r}
            pipeline="padel"
            leadState={leadStates[lk('padel',r.id)]}
            profiles={profiles}
            myId={profile?.id}
            onRowClick={onRowClick}
            onQuickStatus={onQuickStatus}
            onEdit={r => setEditLead(r)}
            C={C}
          />
        ))}
      </div>

      {addOpen && <LeadFormModal pipeline="padel" onSave={handleSaved} onClose={()=>setAddOpen(false)} onDelete={handleDeleted} C={C}/>}
      {editLead && <LeadFormModal pipeline="padel" lead={editLead} onSave={handleSaved} onClose={()=>setEditLead(null)} onDelete={handleDeleted} C={C}/>}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════ */
export default function SnowsProAmCRM() {

  const [accessGranted, setAccessGranted] = useState(() => {
    try { return localStorage.getItem('crm-access') === 'ok' } catch { return false }
  })

  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('crm-theme') !== 'light' } catch { return true }
  })
  const C = isDark ? DARK : LIGHT
  const toggleTheme = () => setIsDark(v => {
    const n = !v
    try { localStorage.setItem('crm-theme', n ? 'dark' : 'light') } catch {}
    return n
  })

  const [authUser, setAuthUser] = useState(undefined)
  const [allProfiles,  setAllProfiles]  = useState(null)
  const [selectedProf, setSelectedProf] = useState(null)
  const [crmProfile,   setCrmProfile]   = useState(null)
  const [pinPassed,    setPinPassed]    = useState(false)
  const [showSetup,    setShowSetup]    = useState(false)
  const [editProfileOpen, setEditProfileOpen] = useState(false)

  const [schools,    setSchools]    = useState([])
  const [ukSchools,  setUKSchools]  = useState([])
  const [padel,      setPadel]      = useState([])
  const [leadStates, setLeadStates] = useState({})
  const [activities, setActivities] = useState([])
  const [profiles,   setProfiles]   = useState({})
  const [online,     setOnline]     = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [dataError, setDataError] = useState('')

  const [page,     setPage]     = useState('dashboard')
  const [feedOpen, setFeedOpen] = useState(false)
  const [modal,    setModal]    = useState(null)
  const [toasts,   setToasts]   = useState([])
  const [unread,   setUnread]   = useState(0)

  const channelsRef = useRef([])

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setAuthUser(user ?? null)
      if (!user) setAllProfiles([])
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_evt, session) => {
      const user = session?.user ?? null
      setAuthUser(user)
      if (!user) { setPinPassed(false); setCrmProfile(null); setAllProfiles([]); setSelectedProf(null) }
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!authUser) return
    supabase.from('crm_profiles').select('*').order('display_name')
      .then(({ data }) => setAllProfiles(data || []))
  }, [authUser])

  const loadPipeline = async pipeline => {
    const rows = []
    for (let start = 0; ; start += 500) {
      const { data, error } = await supabase.from('crm_pipeline_leads').select('*').eq('pipeline',pipeline).order('id').range(start,start+499)
      if (error) throw error
      rows.push(...(data || []))
      if (!data || data.length < 500) break
    }
    return rows
  }

  const loadAllStates = async () => {
    const rows = []
    for (let start = 0; ; start += 500) {
      const { data, error } = await supabase.from('crm_lead_states').select('*').order('id').range(start,start+499)
      if (error) throw error
      rows.push(...(data || []))
      if (!data || data.length < 500) break
    }
    return rows
  }

  const loadAll = useCallback(async () => {
    setDataError('')
    try {
    const [
      schoolsData,
      ukSchoolsData,
      padelData,
      states,
      { data: acts },
      { data: profs },
    ] = await Promise.all([
      loadPipeline('schools'),
      loadPipeline('schools_uk'),
      loadPipeline('padel'),
      loadAllStates(),
      supabase.from('crm_activity_log').select('*').order('created_at',{ascending:false}).limit(200),
      supabase.from('crm_profiles').select('*'),
    ])
    if (schoolsData) setSchools(schoolsData)
    if (ukSchoolsData) setUKSchools(ukSchoolsData)
    if (padelData)   setPadel(padelData)
    if (states) { const m={}; states.forEach(s=>{ m[s.entity_key]=s }); setLeadStates(m) }
    if (acts)   setActivities(acts)
    if (profs)  { const m={}; profs.forEach(p=>{ m[p.id]=p }); setProfiles(m) }
    setDataLoaded(true)
    } catch (error) { setDataError(error.message || 'Could not load CRM data') }
  }, [])

  const setupRealtime = useCallback((prof) => {
    const crmCh = supabase.channel('crm-realtime')
      .on('postgres_changes',{event:'*',schema:'public',table:'crm_lead_states'},payload=>{
        setLeadStates(prev=>({...prev,[payload.new.entity_key]:payload.new}))
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'crm_activity_log'},payload=>{
        const a = payload.new
        setActivities(prev=>[a,...prev].slice(0,200))
        if (a.user_id !== prof.id) {
          setProfiles(prev=>{
            const caller = prev[a.user_id]
            const id = Date.now()
            const sm = STATUS_MAP[a.status_to||'']
            setToasts(t=>[...t,{id,emoji:caller?.emoji||'⚡',color:caller?.color||C.accent,title:caller?.display_name||a.user_id,body:`${sm?.label||'Updated'} → ${a.entity_name}`}])
            setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),4500)
            return prev
          })
          setUnread(u=>u+1)
        }
      })
      .on('postgres_changes',{event:'*',schema:'public',table:'crm_profiles'},payload=>{
        setProfiles(prev=>({...prev,[payload.new.id]:payload.new}))
        setAllProfiles(prev=>(prev||[]).map(p=>p.id===payload.new.id?payload.new:p))
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'crm_pipeline_leads'},payload=>{
        const lead = payload.new
        if (lead.pipeline === 'schools') setSchools(prev=>[...prev, lead])
        if (lead.pipeline === 'schools_uk') setUKSchools(prev=>[...prev, lead])
        if (lead.pipeline === 'padel')   setPadel(prev=>[...prev, lead])
      })
      .subscribe()

    const presCh = supabase.channel('pa-presence')
      .on('presence',{event:'sync'},()=>{ setOnline(Object.values(presCh.presenceState()).flat()) })
      .subscribe(async status=>{
        if (status==='SUBSCRIBED') {
          await presCh.track({user_id:prof.id,name:prof.display_name,color:prof.color,emoji:prof.emoji,online_at:new Date().toISOString()})
        }
      })

    channelsRef.current = [crmCh, presCh]
    return () => channelsRef.current.forEach(ch=>supabase.removeChannel(ch))
  }, [C.accent])

  useEffect(() => {
    if (!pinPassed || !crmProfile) return
    loadAll()
    const cleanup = setupRealtime(crmProfile)
    return cleanup
  }, [pinPassed, crmProfile])

  const updateLead = async ({ key, status, notes, entityName, pipeline }) => {
    const existing = leadStates[key] || {}
    const now = new Date().toISOString()

    if (!status) {
      const { data: myRecent } = await supabase.from('crm_activity_log').select('id').eq('entity_key', key).eq('user_id', crmProfile.id).neq('status_to', '').order('created_at', { ascending: false }).limit(1).maybeSingle()
      if (myRecent?.id) { await supabase.from('crm_activity_log').delete().eq('id', myRecent.id); setActivities(prev => prev.filter(a => a.id !== myRecent.id)) }
      const { data: otherRecent } = await supabase.from('crm_activity_log').select('user_id, created_at, status_to').eq('entity_key', key).neq('user_id', crmProfile.id).neq('status_to', '').order('created_at', { ascending: false }).limit(1).maybeSingle()
      const updatedState = otherRecent
        ? { pipeline, entity_key: key, status: otherRecent.status_to, notes: notes || existing.notes || '', last_called_by: otherRecent.user_id, last_called_at: otherRecent.created_at, call_count: Math.max((existing.call_count||1) - 1, 0), updated_at: now }
        : { pipeline, entity_key: key, status: '', notes: notes || existing.notes || '', last_called_by: null, last_called_at: null, call_count: 0, updated_at: now }
      const { data } = await supabase.from('crm_lead_states').upsert(updatedState, { onConflict: 'pipeline,entity_key' }).select().single()
      if (data) setLeadStates(prev=>({...prev,[key]:data}))
    } else {
      const { data } = await supabase.from('crm_lead_states').upsert({
        pipeline, entity_key: key, status, notes,
        last_called_by: crmProfile.id,
        call_count: status !== existing.status ? (existing.call_count||0)+1 : (existing.call_count||0),
        last_called_at: now, updated_at: now,
      }, { onConflict: 'pipeline,entity_key' }).select().single()
      if (data) setLeadStates(prev=>({...prev,[key]:data}))
      await supabase.from('crm_activity_log').insert({ pipeline, entity_key: key, entity_name: entityName, user_id: crmProfile.id, action: 'call', status_from: existing.status||'', status_to: status, notes })
    }
  }

  const quickStatus = (lead, pipeline, nextStatus) => {
    const key = lk(pipeline, lead.id)
    updateLead({ key, status: nextStatus, notes: (leadStates[key]||{}).notes||'', entityName: lead.name, pipeline })
  }

  const lockCRM = () => {
    channelsRef.current.forEach(ch=>supabase.removeChannel(ch))
    setPinPassed(false); setCrmProfile(null); setSelectedProf(null)
    setLeadStates({}); setActivities([]); setOnline([]); setDataLoaded(false); setDataError('')
  }

  const handleProfileCreated = (prof) => {
    setAllProfiles(prev => [...(prev||[]), prof])
    setProfiles(prev => ({...prev, [prof.id]: prof}))
    setCrmProfile(prof); setPinPassed(true); setShowSetup(false)
  }

  const handleEditProfileSaved = (updated) => {
    setCrmProfile(updated)
    setProfiles(prev => ({...prev, [updated.id]: updated}))
    setAllProfiles(prev => (prev||[]).map(p => p.id===updated.id ? updated : p))
  }

  // ── render guards ──

  if (!accessGranted)
    return <><style>{makeCSS(C)}</style><AccessGate onSuccess={() => setAccessGranted(true)} C={C}/></>

  if (authUser === undefined)
    return <><style>{makeCSS(C)}</style><LoadingScreen msg="Checking session…" C={C}/></>

  if (!authUser)
    return <><style>{makeCSS(C)}</style><NotAuthed C={C}/></>

  if (allProfiles === null)
    return <><style>{makeCSS(C)}</style><LoadingScreen msg="Loading profiles…" C={C}/></>

  if (showSetup)
    return <><style>{makeCSS(C)}</style><ProfileSetup authUser={authUser} onComplete={handleProfileCreated} onBack={()=>setShowSetup(false)} C={C}/></>

  if (!selectedProf)
    return <><style>{makeCSS(C)}</style><ProfileSwitcher allProfiles={allProfiles} onSelect={p => setSelectedProf(p)} onCreateNew={() => setShowSetup(true)} C={C}/></>

  if (!pinPassed)
    return <><style>{makeCSS(C)}</style><PinLogin profile={selectedProf} onSuccess={() => { setCrmProfile(selectedProf); setPinPassed(true) }} onBack={() => setSelectedProf(null)} C={C}/></>

  if (!dataLoaded)
    return <><style>{makeCSS(C)}</style>{dataError ? <div style={{padding:32,color:C.text}}>Could not load pipeline data: {dataError} <button onClick={loadAll}>Retry</button></div> : <LoadingScreen msg="Loading pipeline data…" C={C}/>}</>

  return (
    <>
      <style>{makeCSS(C)}</style>
      <Header
        profile={crmProfile} page={page} setPage={setPage}
        onlineUsers={online} unread={unread}
        onFeed={()=>{setFeedOpen(true);setUnread(0)}}
        onLock={lockCRM}
        onEditProfile={() => setEditProfileOpen(true)}
        isDark={isDark} onTheme={toggleTheme}
        C={C}
      />

      {/* Mobile bottom navigation */}
      <BottomNav page={page} setPage={setPage} C={C}/>

      <ActivityDrawer open={feedOpen} onClose={()=>setFeedOpen(false)} activities={activities} profiles={profiles} myId={crmProfile.id} C={C}/>
      <Toast toasts={toasts}/>

      {editProfileOpen && (
        <EditProfileModal
          profile={crmProfile}
          onSave={handleEditProfileSaved}
          onClose={() => setEditProfileOpen(false)}
          C={C}
        />
      )}

      {page==='dashboard' && <DashboardPage leadStates={leadStates} profiles={profiles} schoolCount={schools.length} ukSchoolCount={ukSchools.length} padelCount={padel.length} C={C}/>}
      {page==='schools' && (
        <SchoolsPage schools={schools} setSchools={setSchools} leadStates={leadStates} profile={crmProfile} profiles={profiles}
          onRowClick={(lead,pipeline)=>setModal({lead,pipeline})} onQuickStatus={quickStatus} C={C}/>
      )}
      {page==='schools_uk' && (
        <UKSchoolsPage schools={ukSchools} setSchools={setUKSchools} leadStates={leadStates} profile={crmProfile} profiles={profiles}
          onRowClick={(lead,pipeline)=>setModal({lead,pipeline})} onQuickStatus={quickStatus} C={C}/>
      )}
      {page==='padel' && (
        <PadelPage padel={padel} setPadel={setPadel} leadStates={leadStates} profile={crmProfile} profiles={profiles}
          onRowClick={(lead,pipeline)=>setModal({lead,pipeline})} onQuickStatus={quickStatus} C={C}/>
      )}

      {modal && (
        <LeadModal lead={modal.lead} pipeline={modal.pipeline} leadStates={leadStates} profile={crmProfile} profiles={profiles}
          onSave={updateLead} onClose={()=>setModal(null)} C={C}/>
      )}
    </>
  )
}
